import { useEffect, useMemo, useRef, useState, isValidElement } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    serverTimestamp,
    Timestamp,
    updateDoc,
    writeBatch,
    where,
    DocumentReference,
    QueryConstraint,
    or,
    and,
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { auth, db, storage } from '@/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { formatPhoneNumber } from '../../../utils/phone';
import { getMailNotificationSettings } from '@/utils/mailNotificationSettings';

interface NormalizedData {
    [key: string]: any;
}

interface MemoItem {
    id: string;
    CreateAt?: Timestamp | Date | null;
    text?: string;
    CreateUser?: DocumentReference | string;
    TGTorder?: DocumentReference;
    TGTuser?: DocumentReference | null;
    task?: string | null;
    tasklimit?: Timestamp | Date | null;
    account?: DocumentReference | string | null;
}

interface MemoDetailData {
    memo: MemoItem;
    createUserName: string;
    targetUserName: string;
    shikakuName: string;
}

interface PaymentRecord {
    id: string;
    installmentNumber?: number;
    amount?: number;
    deposit?: number;
    paycheck?: boolean;
    dueDate?: Timestamp | Date | string | null;
    paidAt?: Timestamp | Date | string | null;
    paymentDate?: Timestamp | Date | string | null;
}

interface BankdataRecord {
    id: string;
    depositdate?: Timestamp | Date | string | null;
    incash?: number;
    name?: string;
}

interface PaymentEditRecord {
    id: string;
    dueDate: string;
    amount: string;
    deposit: string;
    paycheck: boolean;
    paidAt: string;
}

interface NewPaymentForm {
    dueDate: string;
    amount: string;
    deposit: string;
    paycheck: boolean;
    paidAt: string;
}

const FIELD_LABELS = {
    orderId: '注文ID',
    sfId: 'SFID',
    status: 'ステータス',
    paymentMethod: '支払方法',
    totalAmount: '注文金額',
    UserRef: '氏名',
    accessories: '付属品',
    cancelledAt: 'キャンセル日時',
    orderNumber: '注文番号',
    createdAt: '注文日時',
    shikakuId: '資格名',
    expiresAt: '有効期限',
    course: 'コース',
    diploma: '認定証',
    postalCode: '郵便番号',
    address: '住所',
    paymentStatus: '支払ステータス',
    Hstatus: '本試験ステータス',
    testtype: '本試験タイプ',
    Tstatus: '添削ステータス',
    TarvDate: '到着',
    HarvDate: '到着',
    TsnDDate: '送付日',
    HsnDDate: '送付日',
    trackingNumbers: '商品発送番号',
    DtrackingNumber: '認定証追跡番号',
    HsndDate: '本試験返送日',
    Dstatus: '認定証ステータス',
    TsndDate: '添削返送日',
    DsendDate: '認定証返送日',
    trouble: '要注意',
    lost: '貸倒',
};

const RECEIPT_STYLE = `
body {
    font-family: "Helvetica Neue", Arial, "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif;
    background-color: #f0f0f0;
    padding: 20px;
    color: #333;
}

.receipt-container {
    max-width: 800px;
    margin: 0 auto;
    background-color: #fff;
    padding: 40px;
    border: 1px solid #ccc;
    box-shadow: 0 0 10px rgba(0,0,0,0.1);
}

.receipt-container header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    border-bottom: 2px solid #000;
    margin-bottom: 20px;
    padding-bottom: 10px;
}

.receipt-container h1 {
    margin: 0;
    font-size: 28px;
    letter-spacing: 0.5em;
}

.date-section p {
    margin: 0;
    font-size: 0.9em;
    text-align: right;
}

.recipient {
    margin-bottom: 30px;
}

.recipient-name {
    border-bottom: 1px solid #333;
    display: inline-block;
    padding-right: 50px;
    margin-bottom: 10px;
}

.total-amount {
    text-align: center;
    border: 2px solid #333;
    padding: 15px;
    margin-bottom: 30px;
    background-color: #f9f9f9;
}

.total-amount .price {
    font-size: 32px;
    font-weight: bold;
    margin: 0 10px;
    border-bottom: 1px double #333;
}

.main-content {
    display: flex;
    justify-content: space-between;
    gap: 20px;
}

.details-table {
    width: 65%;
    border-collapse: collapse;
}

.details-table th,
.details-table td {
    border: 1px solid #ccc;
    padding: 10px;
    text-align: left;
}

.details-table th {
    background-color: #eee;
}

.issuer-info {
    width: 30%;
    font-size: 0.85em;
    line-height: 1.6;
}

.issuer-info .company-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.receipt-seal {
    width: 21mm;
    height: 21mm;
    object-fit: contain;
}

.company-name {
    font-weight: bold;
    font-size: 1.1em;
    margin-bottom: 5px;
}

.summary-section {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}

.remarks {
    font-size: 0.9em;
    color: #666;
}

.calculation-table {
    width: 200px;
    border-collapse: collapse;
}

.calculation-table th,
.calculation-table td {
    padding: 5px;
    text-align: right;
    border-bottom: 1px solid #eee;
}

.grand-total {
    font-weight: bold;
    font-size: 1.1em;
}
`;

interface ShikakuOption {
    id: string;
    name: string;
}

interface MailTemplate {
    id: string;
    title: string;
    subject: string;
    body: string;
    sign?: string;
    notificationType?: string;
}

interface MailMessageItem {
    id: string;
    createdAt?: Timestamp | Date | null;
    direction?: string;
    subject?: string;
    body?: string;
    from?: string;
    to?: string;
    customerEmail?: string;
}

interface AdminUserOption {
    id: string;
    displayName: string;
}

interface TemplateContext {
    order: Record<string, any>;
    user: Record<string, any>;
    shikaku: Record<string, any>;
}

interface OrderEditForm {
    status: string;
    trouble: boolean;
    lost: boolean;
    totalAmount: string;
    paymentMethod: string;
    orderNumber: string;
    course: string;
    diploma: string;
    accessories: string;
    expiresAt: string;
    Hstatus: string;
    testtype: string;
    Tstatus: string;
    HarvDate: string;
    TarvDate: string;
    trackingNumbers: string;
    DtrackingNumber: string;
    HsndDate: string;
    Dstatus: string;
    TsndDate: string;
    DsendDate: string;
}

type OrderTab = 'all' | 'shipping' | 'tensaku' | 'exam' | 'graded';
type ReceiptSealImage = {
    data: Uint8Array;
    width: number;
    height: number;
};

type ReceiptCanvasImage = {
    data: Uint8Array;
    width: number;
    height: number;
};

const normalizeValue = (value: any, key?: string, seen: WeakSet<object> = new WeakSet()): any => {
    if (value === null || value === undefined) return value;

    if (value instanceof Timestamp) {
        return value.toDate().toLocaleString('ja-JP');
    }

    if (value instanceof DocumentReference) {
        return value.path || value.id;
    }

    if (isValidElement(value)) {
        return value;
    }

    if (typeof value === 'string' && key?.toLowerCase().includes('phone')) {
        return formatPhoneNumber(value);
    }

    if (typeof value === 'object') {
        if (seen.has(value)) {
            return '[Circular]';
        }
        seen.add(value);

        if (Array.isArray(value)) {
            return value.map((item) => normalizeValue(item, undefined, seen));
        }

        return Object.fromEntries(
            Object.entries(value).map(([childKey, val]) => [childKey, normalizeValue(val, childKey, seen)])
        );
    }

    return value;
};

export default function OrderDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { userId: currentUserId } = useAuth();
    const [orderData, setOrderData] = useState<any>(null);
    const [userName, setUserName] = useState<string>('');
    const [userId, setUserId] = useState<string>('');
    const [userEmail, setUserEmail] = useState<string>('');
    const [userPostalCode, setUserPostalCode] = useState<string>('');
    const [userAddress, setUserAddress] = useState<string>('');
    const [shikakus, setShikakus] = useState<ShikakuOption[]>([]);
    const [selectedShikakuId, setSelectedShikakuId] = useState<string>('');
    const [initialShikakuId, setInitialShikakuId] = useState<string>('');
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState<OrderEditForm>({
        status: '',
        trouble: false,
        lost: false,
        totalAmount: '',
        paymentMethod: '',
        orderNumber: '',
        course: '',
        diploma: '',
        accessories: '',
        expiresAt: '',
        Hstatus: '',
        testtype: '',
        Tstatus: '',
        HarvDate: '',
        TarvDate: '',
        trackingNumbers: '',
        DtrackingNumber: '',
        HsndDate: '',
        Dstatus: '',
        TsndDate: '',
        DsendDate: '',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);
    const [trackingInput, setTrackingInput] = useState('');
    const [trackingNumbers, setTrackingNumbers] = useState<string[]>([]);
    const [shippingSaving, setShippingSaving] = useState(false);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [taskTrackingInput, setTaskTrackingInput] = useState('');
    const [skipTaskMail, setSkipTaskMail] = useState(false);
    const [taskSaving, setTaskSaving] = useState(false);
    const [isExamModalOpen, setIsExamModalOpen] = useState(false);
    const [examSaving, setExamSaving] = useState(false);
    const [isDiplomaModalOpen, setIsDiplomaModalOpen] = useState(false);
    const [diplomaTrackingInput, setDiplomaTrackingInput] = useState('');
    const [isAcrylicShipping, setIsAcrylicShipping] = useState(false);
    const [diplomaSaving, setDiplomaSaving] = useState(false);
    const [activeTab, setActiveTab] = useState<'mail' | 'memo'>('mail');
    const [memos, setMemos] = useState<MemoItem[]>([]);
    const [memoLoading, setMemoLoading] = useState(false);
    const [memoError, setMemoError] = useState<string | null>(null);
    const [isMemoModalOpen, setIsMemoModalOpen] = useState(false);
    const [memoText, setMemoText] = useState('');
    const [memoSaving, setMemoSaving] = useState(false);
    const [memoCreatedAt, setMemoCreatedAt] = useState<Date | null>(null);
    const [memoDetail, setMemoDetail] = useState<MemoDetailData | null>(null);
    const [isMemoDetailOpen, setIsMemoDetailOpen] = useState(false);
    const [isMemoDetailEditing, setIsMemoDetailEditing] = useState(false);
    const [memoDetailText, setMemoDetailText] = useState('');
    const [memoDetailSaving, setMemoDetailSaving] = useState(false);
    const [memoTaskStatus, setMemoTaskStatus] = useState<'active' | 'done' | null>(null);
    const [memoTaskLimit, setMemoTaskLimit] = useState('');
    const [memoAccountId, setMemoAccountId] = useState('');
    const [adminUsers, setAdminUsers] = useState<AdminUserOption[]>([]);
    const [currentStaffName, setCurrentStaffName] = useState('');
    const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
    const [receiptIssueDate, setReceiptIssueDate] = useState('');
    const [receiptApplicationDate, setReceiptApplicationDate] = useState('');
    const [receiptCustomerName, setReceiptCustomerName] = useState('');
    const [receiptRemark, setReceiptRemark] = useState('ーー');
    const [receiptDownloadUrl, setReceiptDownloadUrl] = useState('');
    const [receiptSaving, setReceiptSaving] = useState(false);
    const [receiptSealUrl, setReceiptSealUrl] = useState('');
    const [receiptSealImage, setReceiptSealImage] = useState<ReceiptSealImage | null>(null);
    const [mailNotificationsEnabled, setMailNotificationsEnabled] = useState(true);
    const [mailMessages, setMailMessages] = useState<MailMessageItem[]>([]);
    const [mailLoading, setMailLoading] = useState(false);
    const [mailError, setMailError] = useState<string | null>(null);
    const [mailPage, setMailPage] = useState(1);
    const [selectedMailMessage, setSelectedMailMessage] = useState<MailMessageItem | null>(null);
    const [payments, setPayments] = useState<PaymentRecord[]>([]);
    const [paymentEdits, setPaymentEdits] = useState<PaymentEditRecord[]>([]);
    const [newPaymentForm, setNewPaymentForm] = useState<NewPaymentForm>({
        dueDate: '',
        amount: '',
        deposit: '',
        paycheck: true,
        paidAt: '',
    });
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [paymentsError, setPaymentsError] = useState<string | null>(null);
    const [bankdataItems, setBankdataItems] = useState<BankdataRecord[]>([]);
    const [bankdataLoading, setBankdataLoading] = useState(false);
    const [bankdataError, setBankdataError] = useState<string | null>(null);
    const [prevOrderId, setPrevOrderId] = useState<string | null>(null);
    const [nextOrderId, setNextOrderId] = useState<string | null>(null);
    const [navigationLoading, setNavigationLoading] = useState(false);
    const paymentEditById = useMemo(
        () => new Map(paymentEdits.map((payment) => [payment.id, payment])),
        [paymentEdits]
    );
    const receiptRef = useRef<HTMLDivElement | null>(null);
    const listTab = useMemo((): OrderTab => {
        const tab = searchParams.get('tab');
        if (tab === 'shipping' || tab === 'tensaku' || tab === 'exam' || tab === 'graded' || tab === 'all') {
            return tab;
        }
        return 'all';
    }, [searchParams]);

    const listQueryString = useMemo(() => {
        if (listTab === 'all') {
            return '';
        }
        const params = new URLSearchParams();
        params.set('tab', listTab);
        const queryString = params.toString();
        return queryString ? `?${queryString}` : '';
    }, [listTab]);

    useEffect(() => {
        if (isTaskModalOpen) {
            setSkipTaskMail(false);
        }
    }, [isTaskModalOpen]);

    useEffect(() => {
        if (isDiplomaModalOpen) {
            setIsAcrylicShipping(false);
        }
    }, [isDiplomaModalOpen]);

    const getOrderDetailLink = (orderId: string) => {
        return listQueryString ? `/admin/orders/${orderId}${listQueryString}` : `/admin/orders/${orderId}`;
    };

    const toLocalDateTimeInput = (date: Date) => {
        const pad = (value: number) => String(value).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
            date.getMinutes()
        )}`;
    };

    const toDateInputValue = (value?: Timestamp | Date | string | null) => {
        if (!value) return '';
        const date =
            value instanceof Timestamp
                ? value.toDate()
                : value instanceof Date
                  ? value
                  : new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        const pad = (val: number) => String(val).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    };

    const defaultDateInputValue = () => toDateInputValue(new Date());

    const formatTrackingNumbers = (values?: unknown) => {
        if (Array.isArray(values)) {
            return values.map((value) => String(value)).filter((value) => value.trim() !== '').join('\n');
        }
        return values ? String(values) : '';
    };

    const parseTrackingNumbers = (value: string) =>
        value
            .split(/[\n,、]/g)
            .map((item) => item.trim())
            .filter((item) => item !== '');

    const formatCurrency = (value: number) =>
        `¥${Math.round(value).toLocaleString('ja-JP', { maximumFractionDigits: 0 })}`;

    const formatReceiptDate = (value: string) => {
        if (!value) return '';
        const date = new Date(`${value}T00:00:00`);
        if (Number.isNaN(date.getTime())) return value;
        return `${date.getFullYear()}年 ${date.getMonth() + 1}月 ${date.getDate()}日`;
    };

    const getOrderCreatedAtValue = () =>
        orderData?.CreateAt ?? orderData?.createdAt ?? orderData?.created_at ?? orderData?.createAt ?? null;

    const getTotalAmountValue = () => {
        const raw = orderData?.totalAmount ?? orderData?.total_amount ?? orderData?.total;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const getReceiptSubtotal = () => {
        const total = getTotalAmountValue();
        return total ? Math.floor(total / 1.1) : 0;
    };

    const getReceiptTax = () => {
        const total = getTotalAmountValue();
        return total ? total - getReceiptSubtotal() : 0;
    };

    const renderReceiptCanvasImage = async (): Promise<ReceiptCanvasImage> => {
        const pageWidth = 595.28;
        const pageHeight = 841.89;
        const scale = 2;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(pageWidth * scale);
        canvas.height = Math.round(pageHeight * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('領収書キャンバスの生成に失敗しました');
        }
        ctx.scale(scale, scale);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageWidth, pageHeight);
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;
        ctx.textBaseline = 'top';

        const setFont = (size: number, weight: 'normal' | 'bold' = 'normal') => {
            ctx.font = `${weight} ${size}px "Noto Sans JP", "Yu Gothic", "Hiragino Kaku Gothic ProN", "Meiryo", sans-serif`;
        };
        const drawText = (x: number, y: number, size: number, text: string, weight: 'normal' | 'bold' = 'normal') => {
            setFont(size, weight);
            ctx.fillStyle = '#000000';
            ctx.fillText(text, x, y);
        };
        const drawLine = (x1: number, y1: number, x2: number, y2: number) => {
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
        };

        const totalAmount = receiptTotalAmount;
        const subtotal = receiptSubtotal;
        const tax = receiptTax;

        drawText(50, 80, 18, '領収書', 'bold');
        drawText(380, 80, 10, `申込日: ${formatReceiptDate(receiptApplicationDate)}`);
        drawText(380, 95, 10, `発行日: ${formatReceiptDate(receiptIssueDate)}`);
        drawLine(50, 110, 545, 110);

        drawText(50, 140, 12, `${receiptCustomerName || 'ー'} 様`);
        drawLine(50, 150, 250, 150);
        drawText(50, 170, 10, '下記、正に領収いたしました。');
        drawText(50, 190, 10, '件名: 資格取得講座受講代金として');

        drawLine(50, 220, 545, 220);
        drawText(250, 245, 12, '合計金額', 'bold');
        drawText(340, 245, 14, formatCurrency(totalAmount), 'bold');
        drawText(460, 245, 10, '(税込)');
        drawLine(50, 265, 545, 265);

        drawText(50, 300, 10, 'No.');
        drawText(90, 300, 10, '摘要');
        drawText(280, 300, 10, '数量');
        drawText(340, 300, 10, '単価');
        drawText(430, 300, 10, '金額');
        drawLine(50, 310, 545, 310);
        drawText(50, 330, 10, '1');
        drawText(90, 330, 10, shikakuName ? `${shikakuName}資格取得講座` : '資格取得講座');
        drawText(280, 330, 10, '1');
        drawText(340, 330, 10, formatCurrency(subtotal));
        drawText(430, 330, 10, formatCurrency(subtotal));
        drawLine(50, 350, 545, 350);

        drawText(360, 380, 10, '株式会社RIGWORKS', 'bold');
        if (receiptSealImage) {
            const sealSize = (21 / 25.4) * 72;
            const sealBlob = new Blob([receiptSealImage.data], { type: 'image/jpeg' });
            const sealBitmap = await createImageBitmap(sealBlob);
            ctx.drawImage(sealBitmap, 480, 362, sealSize, sealSize);
        }
        drawText(360, 395, 9, '通信資格のラーキャリ');
        drawText(360, 410, 9, '〒619-0237');
        drawText(360, 425, 9, '京都府相楽郡精華町光台1丁目7');
        drawText(360, 440, 9, 'けいはんなプラザ11F');
        drawText(360, 455, 9, 'TEL: 0774-51-5638');
        drawText(360, 470, 9, 'E-Mail: info@lear-caree.jp');
        drawText(360, 485, 9, '登録番号: T8130001069799');

        drawText(50, 520, 10, `備考: ${receiptRemark}`);
        drawText(380, 520, 10, '小計');
        drawText(500, 520, 10, formatCurrency(subtotal));
        drawText(380, 535, 10, '消費税');
        drawText(500, 535, 10, formatCurrency(tax));
        drawText(380, 550, 11, '合計', 'bold');
        drawText(500, 550, 11, formatCurrency(totalAmount), 'bold');

        const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob((result) => {
                if (result) {
                    resolve(result);
                } else {
                    reject(new Error('領収書画像の生成に失敗しました'));
                }
            }, 'image/jpeg');
        });
        const buffer = await blob.arrayBuffer();
        return { data: new Uint8Array(buffer), width: canvas.width, height: canvas.height };
    };

    const buildReceiptPdfBlob = async () => {
        const encoder = new TextEncoder();
        const toBytes = (value: string) => encoder.encode(value);
        const pageWidth = 595.28;
        const pageHeight = 841.89;
        const canvasImage = await renderReceiptCanvasImage();
        const contentStream = `q ${pageWidth} 0 0 ${pageHeight} 0 0 cm /Im1 Do Q`;
        const contentLength = toBytes(contentStream).length;
        const parts: Uint8Array[] = [];
        const offsets: number[] = [0];
        let byteLength = 0;
        const pushPart = (chunk: Uint8Array) => {
            parts.push(chunk);
            byteLength += chunk.length;
        };
        const pushObject = (chunks: Uint8Array | Uint8Array[]) => {
            offsets.push(byteLength);
            if (Array.isArray(chunks)) {
                chunks.forEach(pushPart);
            } else {
                pushPart(chunks);
            }
        };

        pushPart(toBytes('%PDF-1.4\n'));
        pushObject(toBytes('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n'));
        pushObject(toBytes('2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n'));
        pushObject(
            toBytes(
                `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /XObject << /Im1 4 0 R >> >> /Contents 5 0 R >>\nendobj\n`
            )
        );
        pushObject([
            toBytes(
                `4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${canvasImage.width} /Height ${canvasImage.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${canvasImage.data.length} >>\nstream\n`
            ),
            canvasImage.data,
            toBytes('\nendstream\nendobj\n'),
        ]);
        pushObject(toBytes(`5 0 obj\n<< /Length ${contentLength} >>\nstream\n${contentStream}\nendstream\nendobj\n`));

        const xrefStart = byteLength;
        pushPart(toBytes(`xref\n0 ${offsets.length}\n0000000000 65535 f \n`));
        for (let i = 1; i < offsets.length; i += 1) {
            const offset = String(offsets[i]).padStart(10, '0');
            pushPart(toBytes(`${offset} 00000 n \n`));
        }
        pushPart(toBytes(`trailer\n<< /Size ${offsets.length} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`));

        return new Blob(parts, { type: 'application/pdf' });
    };

    const buildPaymentEdits = (records: PaymentRecord[]): PaymentEditRecord[] =>
        records.map((record) => ({
            id: record.id,
            dueDate: toDateInputValue(record.dueDate),
            amount: record.amount !== undefined && record.amount !== null ? String(record.amount) : '',
            deposit: record.deposit !== undefined && record.deposit !== null ? String(record.deposit) : '',
            paycheck: record.paycheck ?? true,
            paidAt: toDateInputValue(record.paidAt ?? record.paymentDate),
        }));

    const formatDateValue = (value?: Timestamp | Date | string | null) => {
        if (!value) return '-';

        if (value instanceof Timestamp) {
            return value.toDate().toLocaleDateString('ja-JP');
        }

        if (value instanceof Date) {
            return value.toLocaleDateString('ja-JP');
        }

        if (typeof value === 'string') {
            const parsed = new Date(value);
            if (!Number.isNaN(parsed.getTime())) {
                return parsed.toLocaleDateString('ja-JP');
            }
            return value;
        }

        return String(value);
    };

    const createEditForm = (data: any): OrderEditForm => {
        let expiresAtValue = '';
        if (data?.expiresAt) {
            const dateValue =
                data.expiresAt instanceof Timestamp
                    ? data.expiresAt.toDate()
                    : data.expiresAt instanceof Date
                      ? data.expiresAt
                      : new Date(data.expiresAt);
            if (!Number.isNaN(dateValue.getTime())) {
                expiresAtValue = toLocalDateTimeInput(dateValue);
            }
        }

        return {
            status: data?.status ? String(data.status) : '',
            trouble: Boolean(data?.trouble),
            lost: Boolean(data?.lost),
            totalAmount: data?.totalAmount !== null && data?.totalAmount !== undefined ? String(data.totalAmount) : '',
            paymentMethod: data?.paymentMethod ? String(data.paymentMethod) : '',
            orderNumber: data?.orderNumber ? String(data.orderNumber) : '',
            course: data?.course ? String(data.course) : '',
            diploma: data?.diploma ? String(data.diploma) : '',
            accessories: data?.accessories ? String(data.accessories) : '',
            expiresAt: expiresAtValue,
            Hstatus: data?.Hstatus ? String(data.Hstatus) : '',
            testtype: data?.testtype ? String(data.testtype) : '',
            Tstatus: data?.Tstatus ? String(data.Tstatus) : '',
            HarvDate: toDateInputValue(data?.HarvDate),
            TarvDate: toDateInputValue(data?.TarvDate),
            trackingNumbers: formatTrackingNumbers(data?.trackingNumbers),
            DtrackingNumber: data?.DtrackingNumber ? String(data.DtrackingNumber) : '',
            HsndDate: toDateInputValue(data?.HsndDate) || defaultDateInputValue(),
            Dstatus: data?.Dstatus ? String(data.Dstatus) : '',
            TsndDate: toDateInputValue(data?.TsndDate) || defaultDateInputValue(),
            DsendDate: toDateInputValue(data?.DsendDate) || defaultDateInputValue(),
        };
    };

    useEffect(() => {
        const loadMailSettings = async () => {
            try {
                const mailSettings = await getMailNotificationSettings();
                setMailNotificationsEnabled(mailSettings.shipmentNotificationsEnabled);
            } catch (err) {
                console.error('Error loading mail notification settings:', err);
            }
        };

        loadMailSettings();
    }, []);

    useEffect(() => {
        const notify = searchParams.get('notify');
        if (notify === 'task') {
            setIsTaskModalOpen(true);
            return;
        }
        if (notify === 'diploma') {
            setIsDiplomaModalOpen(true);
        }
    }, [searchParams]);

    const buildOrderFilters = (tab: OrderTab) => {
        const filters: QueryConstraint[] = [];
        let orFilter: QueryConstraint | null = null;
        if (tab === 'shipping') {
            filters.push(where('status', '==', 'active'));
            filters.push(where('Pstatus', '==', null));
        } else if (tab === 'tensaku') {
            filters.push(where('Tstatus', '==', 'arrive'));
        } else if (tab === 'exam') {
            filters.push(where('Hstatus', '==', 'arrive'));
        } else if (tab === 'graded') {
            orFilter = or(where('Dstatus', '==', 'ready'), where('Tstatus', '==', 'arrive'));
        }
        if (orFilter && filters.length > 0) {
            return [and(...filters, orFilter)];
        }
        if (orFilter) {
            return [orFilter];
        }
        return filters;
    };

    useEffect(() => {
        const fetchOrder = async () => {
            if (!id) {
                setError('Order IDが指定されていません');
                setLoading(false);
                return;
            }

            try {
                const orderDoc = await getDoc(doc(db, 'Order', id));
                if (!orderDoc.exists()) {
                    setError('Orderが見つかりませんでした');
                    setLoading(false);
                    return;
                }

                const data = { orderId: orderDoc.id, ...orderDoc.data() };
                setOrderData(data);
                setEditForm(createEditForm(data));

                const userRef = data.UserRef || data.userId;
                if (userRef) {
                    const userDocRef = userRef.path ? userRef : doc(db, 'User', userRef);
                    setUserId(userDocRef.id);
                    const userSnapshot = await getDoc(userDocRef);
                    if (userSnapshot.exists()) {
                        const userData = userSnapshot.data();
                        setUserName(userData.display_name || userData.display_Name || '');
                        setUserEmail(userData.email || '');
                        setUserPostalCode(userData.postalCode || userData.postal_code || '');
                        setUserAddress(userData.address || userData.address1 || '');
                    }
                }

                if (data.shikakuId?.id) {
                    setSelectedShikakuId(data.shikakuId.id);
                    setInitialShikakuId(data.shikakuId.id);
                } else if (typeof data.shikakuId === 'string') {
                    setSelectedShikakuId(data.shikakuId);
                    setInitialShikakuId(data.shikakuId);
                }
            } catch (err: any) {
                console.error('Error fetching order detail:', err);
                setError(err?.message || 'Orderの取得に失敗しました');
            } finally {
                setLoading(false);
            }
        };

        fetchOrder();
    }, [id]);

    useEffect(() => {
        const fetchOrderNavigation = async () => {
            const navigationField =
                listTab === 'exam' ? 'HarvDate' : listTab === 'tensaku' ? 'TarvDate' : 'createdAt';
            const navigationValue = orderData?.[navigationField];
            if (!id || !navigationValue) {
                setPrevOrderId(null);
                setNextOrderId(null);
                return;
            }
            setNavigationLoading(true);
            try {
                const tabFilters = buildOrderFilters(listTab);
                const buildNavigationFilters = (createdAtComparison: QueryConstraint) => {
                    if (tabFilters.length === 0) {
                        return [createdAtComparison];
                    }
                    return [and(...tabFilters, createdAtComparison)];
                };
                const nextSnapshot = await getDocs(
                    query(
                        collection(db, 'Order'),
                        ...buildNavigationFilters(where(navigationField, '<', navigationValue)),
                        orderBy(navigationField, 'desc'),
                        limit(1)
                    )
                );
                const prevSnapshot = await getDocs(
                    query(
                        collection(db, 'Order'),
                        ...buildNavigationFilters(where(navigationField, '>', navigationValue)),
                        orderBy(navigationField, 'asc'),
                        limit(1)
                    )
                );
                setNextOrderId(nextSnapshot.docs[0]?.id ?? null);
                setPrevOrderId(prevSnapshot.docs[0]?.id ?? null);
            } catch (err) {
                console.error('Error fetching order navigation:', err);
                setNextOrderId(null);
                setPrevOrderId(null);
            } finally {
                setNavigationLoading(false);
            }
        };

        fetchOrderNavigation();
    }, [id, orderData, listTab]);

    const fetchPayments = async () => {
        if (!id) return;
        setPaymentsLoading(true);
        setPaymentsError(null);
        try {
            const paymentsQuery = query(
                collection(db, 'Order', id, 'payments'),
                orderBy('installmentNumber', 'asc')
            );
            const snapshot = await getDocs(paymentsQuery);
            const results = snapshot.docs.map((docSnapshot) => {
                const data = docSnapshot.data() as Record<string, unknown>;
                return {
                    ...data,
                    id: docSnapshot.id,
                    paycheck: data.paycheck ?? true,
                };
            }) as PaymentRecord[];
            setPayments(results);
            setPaymentEdits(buildPaymentEdits(results));
        } catch (err: any) {
            console.error('Error fetching payments:', err);
            setPaymentsError(err?.message || '入金情報の取得に失敗しました');
        } finally {
            setPaymentsLoading(false);
        }
    };

    useEffect(() => {
        fetchPayments();
    }, [id]);

    const fetchBankdataByOrderRef = async () => {
        if (!id) return;
        setBankdataLoading(true);
        setBankdataError(null);
        try {
            const orderRef = doc(db, 'Order', id);
            const bankdataQuery = query(collection(db, 'bankdata'), where('OrderRef', '==', orderRef));
            const snapshot = await getDocs(bankdataQuery);
            const results = snapshot.docs
                .map((docSnapshot) => {
                    const data = docSnapshot.data() as Record<string, unknown>;
                    return {
                        id: docSnapshot.id,
                        depositdate: (data.depositdate as Timestamp | Date | string | null) ?? null,
                        incash: typeof data.incash === 'number' ? data.incash : Number(data.incash ?? 0),
                        name: typeof data.name === 'string' ? data.name : '-',
                    } satisfies BankdataRecord;
                })
                .sort((a, b) => {
                    const aTime = a.depositdate instanceof Timestamp ? a.depositdate.toMillis() : new Date(a.depositdate ?? 0).getTime();
                    const bTime = b.depositdate instanceof Timestamp ? b.depositdate.toMillis() : new Date(b.depositdate ?? 0).getTime();
                    return bTime - aTime;
                });
            setBankdataItems(results);
        } catch (err: any) {
            console.error('Error fetching bankdata by OrderRef:', err);
            setBankdataError(err?.message || 'bankdataの取得に失敗しました');
        } finally {
            setBankdataLoading(false);
        }
    };

    useEffect(() => {
        fetchBankdataByOrderRef();
    }, [id]);

    useEffect(() => {
        const fetchShikakus = async () => {
            try {
                const snapshot = await getDocs(collection(db, 'Shikaku'));
                const options: ShikakuOption[] = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    name: doc.data().ShikakuName || doc.id,
                }));
                setShikakus(options);
            } catch (err: any) {
                console.error('Error fetching shikaku list:', err);
            }
        };

        fetchShikakus();
    }, []);

    useEffect(() => {
        if (!isShippingModalOpen) return;
        const existingNumbers = Array.isArray(orderData?.trackingNumbers)
            ? orderData.trackingNumbers.map((value: unknown) => String(value))
            : [];
        setTrackingNumbers(existingNumbers);
        setTrackingInput('');
    }, [isShippingModalOpen, orderData]);

    useEffect(() => {
        if (!isTaskModalOpen) return;
        setTaskTrackingInput(orderData?.TtrackingNumber ? String(orderData.TtrackingNumber) : '');
    }, [isTaskModalOpen, orderData]);

    useEffect(() => {
        if (!isDiplomaModalOpen) return;
        setDiplomaTrackingInput(orderData?.DtrackingNumber ? String(orderData.DtrackingNumber) : '');
    }, [isDiplomaModalOpen, orderData]);

    const calculatePaymentTotals = (records: PaymentRecord[]) => {
        const amountTotal = records.reduce((sum, record) => sum + (record.amount ?? 0), 0);
        const depositTotal = records.reduce((sum, record) => sum + (record.deposit ?? 0), 0);
        return { amountTotal, depositTotal, difference: amountTotal - depositTotal };
    };

    const isPaymentStatusComplete = (records: PaymentRecord[]) =>
        records.length > 0 && records.every((record) => Boolean(record.paycheck));

    const isPaymentStatusCompleteFromEdits = (records: PaymentEditRecord[]) =>
        records.length > 0 && records.every((record) => Boolean(record.paycheck));

    const calculateEditTotals = (records: PaymentEditRecord[]) => {
        const amountTotal = records.reduce((sum, record) => {
            const value = Number(record.amount);
            return sum + (Number.isNaN(value) ? 0 : value);
        }, 0);
        const depositTotal = records.reduce((sum, record) => {
            const value = Number(record.deposit);
            return sum + (Number.isNaN(value) ? 0 : value);
        }, 0);
        return { amountTotal, depositTotal, difference: amountTotal - depositTotal };
    };

    const paymentTotals = useMemo(() => calculatePaymentTotals(payments), [payments]);

    const canEditTesttype = ['arrive', 'done'].includes(editForm.Hstatus);

    const handleSave = async () => {
        if (!id || saving) return;
        setSaving(true);
        try {
            const updates: Record<string, any> = {
                status: editForm.status || null,
                trouble: Boolean(editForm.trouble),
                lost: Boolean(editForm.lost),
                paymentMethod: editForm.paymentMethod || null,
                orderNumber: editForm.orderNumber || null,
                course: editForm.course || null,
                diploma: editForm.diploma || null,
                accessories: editForm.accessories || null,
                Hstatus: editForm.Hstatus || null,
                testtype: canEditTesttype ? (editForm.testtype || null) : (orderData?.testtype ?? null),
                Tstatus: editForm.Tstatus || null,
                trackingNumbers: parseTrackingNumbers(editForm.trackingNumbers),
                DtrackingNumber: editForm.DtrackingNumber.trim(),
                Dstatus: editForm.Dstatus || '',
            };
            if (editForm.totalAmount.trim() === '') {
                updates.totalAmount = null;
            } else {
                const amountValue = Number(editForm.totalAmount);
                updates.totalAmount = Number.isNaN(amountValue) ? null : amountValue;
            }

            if (editForm.expiresAt.trim() === '') {
                updates.expiresAt = null;
            } else {
                const expiresAtDate = new Date(editForm.expiresAt);
                updates.expiresAt = Number.isNaN(expiresAtDate.getTime()) ? null : Timestamp.fromDate(expiresAtDate);
            }

            if (editForm.HarvDate.trim() === '') {
                updates.HarvDate = null;
            } else {
                const arrivalDate = new Date(editForm.HarvDate);
                updates.HarvDate = Number.isNaN(arrivalDate.getTime()) ? null : Timestamp.fromDate(arrivalDate);
            }

            if (editForm.TarvDate.trim() === '') {
                updates.TarvDate = null;
            } else {
                const arrivalDate = new Date(editForm.TarvDate);
                updates.TarvDate = Number.isNaN(arrivalDate.getTime()) ? null : Timestamp.fromDate(arrivalDate);
            }

            if (editForm.HsndDate.trim() === '') {
                updates.HsndDate = null;
            } else {
                const sendDate = new Date(editForm.HsndDate);
                updates.HsndDate = Number.isNaN(sendDate.getTime()) ? null : Timestamp.fromDate(sendDate);
            }

            if (editForm.TsndDate.trim() === '') {
                updates.TsndDate = null;
            } else {
                const sendDate = new Date(editForm.TsndDate);
                updates.TsndDate = Number.isNaN(sendDate.getTime()) ? null : Timestamp.fromDate(sendDate);
            }

            if (editForm.DsendDate.trim() === '') {
                updates.DsendDate = null;
            } else {
                const sendDate = new Date(editForm.DsendDate);
                updates.DsendDate = Number.isNaN(sendDate.getTime()) ? null : Timestamp.fromDate(sendDate);
            }

            if (selectedShikakuId) {
                updates.shikakuId = doc(db, 'Shikaku', selectedShikakuId);
            } else {
                updates.shikakuId = null;
            }

            await updateDoc(doc(db, 'Order', id), updates);

            if (paymentEdits.length) {
                const batch = writeBatch(db);
                const paymentsById = new Map(payments.map((payment) => [payment.id, payment]));
                paymentEdits.forEach((edit) => {
                    if (!edit.id) return;
                    if (edit.id === id) {
                        console.warn('Skipped payment edit with order id:', edit.id);
                        return;
                    }
                    const paymentRef = doc(db, 'Order', id, 'payments', edit.id);
                    const updates: Record<string, any> = {};
                    const existing = paymentsById.get(edit.id);
                    const normalize = (value: unknown) => (value === null || value === undefined ? '' : String(value));
                    const existingAmount = existing?.amount ?? null;
                    const existingDeposit = existing?.deposit ?? null;
                    const existingDueDate = existing?.dueDate ?? null;
                    const existingPaidAt = existing?.paidAt ?? existing?.paymentDate ?? null;

                    updates.paycheck = Boolean(edit.paycheck);

                    const amountValueRaw = normalize(edit.amount).trim();
                    if (amountValueRaw !== '' || existingAmount === null || existingAmount === undefined) {
                        if (amountValueRaw === '') {
                            updates.amount = null;
                        } else {
                            const amountValue = Number(amountValueRaw);
                            updates.amount = Number.isNaN(amountValue) ? null : amountValue;
                        }
                    }

                    const depositValueRaw = normalize(edit.deposit).trim();
                    if (depositValueRaw !== '' || existingDeposit === null || existingDeposit === undefined) {
                        if (depositValueRaw === '') {
                            updates.deposit = null;
                        } else {
                            const depositValue = Number(depositValueRaw);
                            updates.deposit = Number.isNaN(depositValue) ? null : depositValue;
                        }
                    }

                    const dueDateValueRaw = normalize(edit.dueDate).trim();
                    if (dueDateValueRaw !== '' || existingDueDate) {
                        if (dueDateValueRaw === '') {
                            updates.dueDate = null;
                        } else {
                            const dueDateValue = new Date(dueDateValueRaw);
                            updates.dueDate = Number.isNaN(dueDateValue.getTime()) ? null : Timestamp.fromDate(dueDateValue);
                        }
                    }

                    const paidAtValueRaw = normalize(edit.paidAt).trim();
                    if (paidAtValueRaw !== '' || existingPaidAt) {
                        if (paidAtValueRaw === '') {
                            updates.paidAt = null;
                        } else {
                            const paidAtValue = new Date(paidAtValueRaw);
                            updates.paidAt = Number.isNaN(paidAtValue.getTime()) ? null : Timestamp.fromDate(paidAtValue);
                        }
                    }

                    if (Object.keys(updates).length > 0) {
                        batch.set(paymentRef, updates, { merge: true });
                    }
                });
                await batch.commit();

                const nextPaymentStatus = isPaymentStatusCompleteFromEdits(paymentEdits);
                await updateDoc(doc(db, 'Order', id), {
                    paymentstatus: nextPaymentStatus,
                });
                await fetchPayments();
            }

            setOrderData((prev: any) => (prev ? { ...prev, ...updates } : prev));
            setInitialShikakuId(selectedShikakuId);
            setIsEditing(false);
            window.location.reload();
        } catch (err: any) {
            console.error('Error updating order detail:', err);
            alert(err?.message || 'Orderの更新に失敗しました');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (orderData) {
            setEditForm(createEditForm(orderData));
        }
        setSelectedShikakuId(initialShikakuId);
        setIsEditing(false);
    };

    const handleAddTrackingNumber = () => {
        const value = trackingInput.trim();
        if (!value) return;
        setTrackingNumbers((prev) => (prev.includes(value) ? prev : [...prev, value]));
        setTrackingInput('');
    };

    const resetNewPaymentForm = () => {
        setNewPaymentForm({
            dueDate: '',
            amount: '',
            deposit: '',
            paycheck: true,
            paidAt: '',
        });
    };

    const handleAddPayment = async () => {
        if (!id) return;
        if (!newPaymentForm.dueDate || newPaymentForm.amount.trim() === '') {
            alert('入金期日と金額を入力してください');
            return;
        }
        const amountValue = Number(newPaymentForm.amount);
        if (Number.isNaN(amountValue)) {
            alert('金額は数値で入力してください');
            return;
        }
        let depositValue: number | null = null;
        if (newPaymentForm.deposit.trim() !== '') {
            const depositInput = Number(newPaymentForm.deposit);
            if (Number.isNaN(depositInput)) {
                alert('入金実績は数値で入力してください');
                return;
            }
            depositValue = depositInput;
        }

        try {
            const installmentNumber =
                payments.reduce((max, payment) => Math.max(max, payment.installmentNumber || 0), 0) + 1;
            const payload = {
                installmentNumber,
                amount: amountValue,
                deposit: depositValue,
                paycheck: newPaymentForm.paycheck,
                dueDate: Timestamp.fromDate(new Date(newPaymentForm.dueDate)),
                paidAt: newPaymentForm.paidAt
                    ? Timestamp.fromDate(new Date(newPaymentForm.paidAt))
                    : null,
            };
            await addDoc(collection(db, 'Order', id, 'payments'), payload);
            resetNewPaymentForm();
            await fetchPayments();
            const refreshedSnapshot = await getDocs(
                query(collection(db, 'Order', id, 'payments'), orderBy('installmentNumber', 'asc'))
            );
            const refreshedPayments = refreshedSnapshot.docs.map((docSnapshot) => ({
                ...(docSnapshot.data() as Record<string, unknown>),
                id: docSnapshot.id,
            })) as PaymentRecord[];
            const totals = calculatePaymentTotals(refreshedPayments);
            const nextPaymentStatus = isPaymentStatusComplete(refreshedPayments);
            await updateDoc(doc(db, 'Order', id), { paymentstatus: nextPaymentStatus, paymentTotals: totals });
        } catch (error) {
            console.error('Error adding payment:', error);
            alert('入金情報の追加に失敗しました');
        }
    };

    const handleDeletePayment = async (paymentId: string) => {
        if (!id) return;
        if (!confirm('この入金情報を削除しますか？')) return;
        try {
            await deleteDoc(doc(db, 'Order', id, 'payments', paymentId));
            await fetchPayments();
            const refreshedSnapshot = await getDocs(
                query(collection(db, 'Order', id, 'payments'), orderBy('installmentNumber', 'asc'))
            );
            const refreshedPayments = refreshedSnapshot.docs.map((docSnapshot) => ({
                ...(docSnapshot.data() as Record<string, unknown>),
                id: docSnapshot.id,
            })) as PaymentRecord[];
            const totals = calculatePaymentTotals(refreshedPayments);
            const nextPaymentStatus = isPaymentStatusComplete(refreshedPayments);
            await updateDoc(doc(db, 'Order', id), { paymentstatus: nextPaymentStatus, paymentTotals: totals });
        } catch (error) {
            console.error('Error deleting payment:', error);
            alert('入金情報の削除に失敗しました');
        }
    };

    const handleRemoveTrackingNumber = (index: number) => {
        setTrackingNumbers((prev) => prev.filter((_, idx) => idx !== index));
    };

    const fetchMailTemplateByNotificationType = async (notificationType: string) => {
        const templateQuery = query(
            collection(db, 'mail_templates'),
            where('notificationType', '==', notificationType),
            limit(1)
        );
        const snapshot = await getDocs(templateQuery);
        if (snapshot.empty) return null;
        const templateDoc = snapshot.docs[0];
        return { id: templateDoc.id, ...(templateDoc.data() as Omit<MailTemplate, 'id'>) };
    };

    const resolveUserEmail = async () => {
        if (userEmail) return userEmail;
        const userRef = orderData?.UserRef || userId;
        if (!userRef) return '';
        const resolvedRef = userRef.path ? userRef : doc(db, 'User', userRef);
        const snapshot = await getDoc(resolvedRef);
        if (!snapshot.exists()) return '';
        const userData = snapshot.data();
        return userData.email || '';
    };

    const buildTemplateContext = async (orderSource: Record<string, any> | null): Promise<TemplateContext> => {
        const orderContext = orderSource ? { ...orderSource } : {};
        let userContext: Record<string, any> = {};
        let shikakuContext: Record<string, any> = {};

        const userRef = orderSource?.UserRef || userId;
        if (userRef) {
            const resolvedRef = userRef.path ? userRef : doc(db, 'User', userRef);
            const snapshot = await getDoc(resolvedRef);
            if (snapshot.exists()) {
                userContext = snapshot.data() as Record<string, any>;
            }
        }

        const shikakuRef = orderSource?.shikakuId;
        if (shikakuRef) {
            const resolvedRef = shikakuRef.path ? shikakuRef : doc(db, 'Shikaku', shikakuRef);
            const snapshot = await getDoc(resolvedRef);
            if (snapshot.exists()) {
                shikakuContext = snapshot.data() as Record<string, any>;
            }
        }

        return {
            order: orderContext,
            user: userContext,
            shikaku: shikakuContext,
        };
    };

    const getTemplateValue = (context: TemplateContext, keyPath: string) => {
        const pathParts = keyPath.split('.');
        if (pathParts.length < 2) return undefined;
        const [rootKey, ...rest] = pathParts;
        let current: any = context[rootKey as keyof TemplateContext];
        for (const part of rest) {
            if (current === null || current === undefined) return undefined;
            if (typeof current !== 'object') return undefined;
            current = Object.prototype.hasOwnProperty.call(current, part) ? current[part] : undefined;
        }
        return current;
    };

    const formatTemplateValue = (value: unknown) => {
        console.log('[mail-template] formatTemplateValue input:', value, 'type:', typeof value);
        if (value === null || value === undefined) return '';
        if (typeof value === 'number') {
            const result = Number.isNaN(value) ? '' : String(value);
            console.log('[mail-template] formatTemplateValue output (number):', result);
            return result;
        }
        if (Array.isArray(value)) {
            const result = value
                .map((item) => formatTemplateValue(item))
                .filter((item) => item !== '')
                .join(', ');
            console.log('[mail-template] formatTemplateValue output (array):', result);
            return result;
        }
        const result = String(normalizeValue(value));
        console.log('[mail-template] formatTemplateValue output (other):', result);
        return result;
    };

    const applyTemplateVariables = (templateText: string, context: TemplateContext) => {
        console.log('[mail-template] applyTemplateVariables context:', {
            order: context.order,
            user: context.user,
            shikaku: context.shikaku,
        });
        return templateText.replace(/{{\s*([\w.]+)\s*}}/g, (_match, key) => {
            console.log('[mail-template] matched key:', key);
            const rawValue = getTemplateValue(context, String(key));
            if (rawValue === undefined) {
                console.log('[mail-template] value missing for key:', key);
            }
            return formatTemplateValue(rawValue);
        });
    };

    const sendShipmentNotification = async (
        recipientEmail: string,
        orderSource: Record<string, any> | null,
        notificationType: string
    ) => {
        const template = await fetchMailTemplateByNotificationType(notificationType);
        if (!template) {
            throw new Error('通知テンプレートが見つかりませんでした。');
        }
        const templateContext = await buildTemplateContext(orderSource);
        console.log('[mail-template] templateContext before render:', templateContext);
        const subject = template.subject || template.title || '商品発送通知';
        const bodyContent = template.sign ? `${template.body}\n\n${template.sign}` : template.body;
        const resolvedSubject = applyTemplateVariables(subject, templateContext);
        const resolvedBody = applyTemplateVariables(bodyContent, templateContext);
        const mailDoc = await addDoc(collection(db, 'mail'), {
            customerEmail: recipientEmail,
            subject: resolvedSubject || '(no subject)',
            status: 'waiting',
            spamLevel: 0,
            createdAt: serverTimestamp(),
            lastMessageAt: serverTimestamp(),
        });

        await addDoc(collection(db, 'mail_messages'), {
            mailId: mailDoc,
            direction: 'out',
            status: 'waiting',
            from: auth.currentUser?.email || '',
            to: recipientEmail,
            subject: resolvedSubject || '(no subject)',
            body: resolvedBody,
            staffUserId: auth.currentUser?.uid,
            createdAt: serverTimestamp(),
        });
    };

    const handleShipOrder = async () => {
        if (!id || shippingSaving) return;
        const inputValue = trackingInput.trim();
        const mergedTrackingNumbers = inputValue
            ? trackingNumbers.includes(inputValue)
                ? trackingNumbers
                : [...trackingNumbers, inputValue]
            : trackingNumbers;
        if (mergedTrackingNumbers.length === 0) return;
        setShippingSaving(true);
        try {
            const updates = {
                Pstatus: 'done',
                trackingNumbers: mergedTrackingNumbers,
            };
            const orderRef = doc(db, 'Order', id);
            await updateDoc(orderRef, updates);
            const refreshedOrderSnapshot = await getDoc(orderRef);
            const refreshedOrderData = refreshedOrderSnapshot.exists()
                ? { orderId: refreshedOrderSnapshot.id, ...refreshedOrderSnapshot.data() }
                : null;
            setOrderData((prev: any) => (refreshedOrderData ? refreshedOrderData : prev ? { ...prev, ...updates } : prev));
            try {
                if (!mailNotificationsEnabled) {
                    alert('メール送信は設定でOFFになっています');
                } else {
                    const email = await resolveUserEmail();
                    if (!email) {
                        alert('ユーザーのメールアドレスが見つかりませんでした');
                    } else {
                        await sendShipmentNotification(email, refreshedOrderData, 'product_shipping');
                    }
                }
            } catch (mailError: any) {
                console.error('Error sending shipment notification:', mailError);
                alert(mailError?.message || '通知メールの送信に失敗しました');
            }
            setIsShippingModalOpen(false);
        } catch (err: any) {
            console.error('Error shipping order:', err);
            alert(err?.message || '発送の更新に失敗しました');
        } finally {
            setShippingSaving(false);
        }
    };

    const handleTaskArrive = async () => {
        if (!id || taskSaving) return;
        setTaskSaving(true);
        try {
            const updates = {
                Tstatus: 'arrive',
                TarvDate: Timestamp.fromDate(new Date()),
            };
            await updateDoc(doc(db, 'Order', id), updates);
            setOrderData((prev: any) => (prev ? { ...prev, ...updates } : prev));
            setIsTaskModalOpen(false);
        } catch (err: any) {
            console.error('Error updating task arrival:', err);
            alert(err?.message || '到着確認の更新に失敗しました');
        } finally {
            setTaskSaving(false);
        }
    };

    const handleTaskShip = async () => {
        if (!id || taskSaving) return;
        const trackingNumber = taskTrackingInput.trim();
        if (!trackingNumber) return;
        setTaskSaving(true);
        try {
            const updates = {
                Tstatus: 'done',
                TsnDDate: Timestamp.fromDate(new Date()),
                TsndDate: Timestamp.fromDate(new Date()),
                TtrackingNumber: trackingNumber,
            };
            const orderRef = doc(db, 'Order', id);
            await updateDoc(orderRef, updates);
            const refreshedOrderSnapshot = await getDoc(orderRef);
            const refreshedOrderData = refreshedOrderSnapshot.exists()
                ? { orderId: refreshedOrderSnapshot.id, ...refreshedOrderSnapshot.data() }
                : null;
            setOrderData((prev: any) => (refreshedOrderData ? refreshedOrderData : prev ? { ...prev, ...updates } : prev));
            try {
                if (skipTaskMail) {
                    alert('メール送信をスキップしました');
                } else if (!mailNotificationsEnabled) {
                    alert('メール送信は設定でOFFになっています');
                } else {
                    const email = await resolveUserEmail();
                    if (!email) {
                        alert('ユーザーのメールアドレスが見つかりませんでした');
                    } else {
                        await sendShipmentNotification(email, refreshedOrderData, 'task_return');
                    }
                }
            } catch (mailError: any) {
                console.error('Error sending task shipment notification:', mailError);
                alert(mailError?.message || '通知メールの送信に失敗しました');
            }
            setIsTaskModalOpen(false);
        } catch (err: any) {
            console.error('Error updating task shipment:', err);
            alert(err?.message || '発送の更新に失敗しました');
        } finally {
            setTaskSaving(false);
        }
    };

    const handleExamArrive = async () => {
        if (!id || examSaving) return;
        setExamSaving(true);
        try {
            const arrivalDate = Timestamp.fromDate(new Date());
            const updates = {
                Hstatus: 'arrive',
                testtype: 'paper',
                HarvDate: arrivalDate,
                HalvDate: arrivalDate,
            };
            await updateDoc(doc(db, 'Order', id), updates);
            setOrderData((prev: any) => (prev ? { ...prev, ...updates } : prev));
            setIsExamModalOpen(false);
        } catch (err: any) {
            console.error('Error updating exam arrival:', err);
            alert(err?.message || '到着確認の更新に失敗しました');
        } finally {
            setExamSaving(false);
        }
    };

    const handleExamDone = async () => {
        if (!id || examSaving) return;
        setExamSaving(true);
        try {
            const updates = {
                Hstatus: 'done',
                Dstatus: 'ready',
                testtype: 'paper',
            };
            await updateDoc(doc(db, 'Order', id), updates);
            setOrderData((prev: any) => (prev ? { ...prev, ...updates } : prev));
            setIsExamModalOpen(false);
        } catch (err: any) {
            console.error('Error updating exam completion:', err);
            alert(err?.message || '採点完了の更新に失敗しました');
        } finally {
            setExamSaving(false);
        }
    };

    const handleDiplomaShip = async () => {
        if (!id || diplomaSaving) return;
        const trackingNumber = diplomaTrackingInput.trim();
        if (!trackingNumber) return;
        setDiplomaSaving(true);
        try {
            const updates = {
                Dstatus: 'done',
                DsendDate: Timestamp.fromDate(new Date()),
                DtrackingNumber: trackingNumber,
            };
            const orderRef = doc(db, 'Order', id);
            await updateDoc(orderRef, updates);
            const refreshedOrderSnapshot = await getDoc(orderRef);
            const refreshedOrderData = refreshedOrderSnapshot.exists()
                ? { orderId: refreshedOrderSnapshot.id, ...refreshedOrderSnapshot.data() }
                : null;
            setOrderData((prev: any) => (refreshedOrderData ? refreshedOrderData : prev ? { ...prev, ...updates } : prev));
            try {
                if (!mailNotificationsEnabled) {
                    alert('メール送信は設定でOFFになっています');
                } else {
                    const email = await resolveUserEmail();
                    if (!email) {
                        alert('ユーザーのメールアドレスが見つかりませんでした');
                    } else {
                        const notificationType = isAcrylicShipping ? 'acrylic_shipping' : 'diploma_shipping';
                        await sendShipmentNotification(email, refreshedOrderData, notificationType);
                    }
                }
            } catch (mailError: any) {
                console.error('Error sending diploma shipment notification:', mailError);
                alert(mailError?.message || '通知メールの送信に失敗しました');
            }
            setIsDiplomaModalOpen(false);
        } catch (err: any) {
            console.error('Error updating diploma shipment:', err);
            alert(err?.message || '発送の更新に失敗しました');
        } finally {
            setDiplomaSaving(false);
        }
    };

    useEffect(() => {
        const fetchMemos = async () => {
            if (!id) return;
            setMemoLoading(true);
            setMemoError(null);
            try {
                const orderRef = doc(db, 'Order', id);
                const memoQuery = query(collection(db, 'Memo'), where('TGTorder', '==', orderRef), orderBy('CreateAt', 'desc'));
                const snapshot = await getDocs(memoQuery);
                const memoItems = snapshot.docs.map((memoDoc) => ({
                    id: memoDoc.id,
                    ...memoDoc.data(),
                })) as MemoItem[];
                setMemos(memoItems);
            } catch (err: any) {
                console.error('Error fetching memos:', err);
                setMemoError(err?.message || 'メモの取得に失敗しました');
            } finally {
                setMemoLoading(false);
            }
        };

        fetchMemos();
    }, [id]);

    useEffect(() => {
        const fetchMailMessages = async () => {
            if (!userEmail) {
                setMailMessages([]);
                return;
            }
            setMailLoading(true);
            setMailError(null);
            try {
                const mailQuery = query(collection(db, 'mail'), where('customerEmail', '==', userEmail));
                const mailSnapshot = await getDocs(mailQuery);
                if (mailSnapshot.empty) {
                    setMailMessages([]);
                    return;
                }

                const messageSnapshots = await Promise.all(
                    mailSnapshot.docs.map(async (mailDoc) => {
                        const customerEmail = (mailDoc.data() as { customerEmail?: string })?.customerEmail || '';
                        const snapshot = await getDocs(
                            query(
                                collection(db, 'mail_messages'),
                                where('mailId', '==', mailDoc.ref),
                                orderBy('createdAt', 'desc')
                            )
                        );
                        return snapshot.docs.map(
                            (messageDoc) =>
                                ({
                                    id: messageDoc.id,
                                    ...(messageDoc.data() as Omit<MailMessageItem, 'id'>),
                                    customerEmail,
                                }) as MailMessageItem
                        );
                    })
                );

                const allMessages = messageSnapshots.flat();

                const sortedMessages = allMessages.sort((a, b) => {
                    const getTimeValue = (value?: Timestamp | Date | null) => {
                        if (!value) return 0;
                        if (value instanceof Timestamp) return value.toDate().getTime();
                        if (value instanceof Date) return value.getTime();
                        return new Date(value as any).getTime();
                    };
                    return getTimeValue(b.createdAt) - getTimeValue(a.createdAt);
                });

                setMailMessages(sortedMessages);
            } catch (err: any) {
                console.error('Error fetching mail messages:', err);
                setMailError(err?.message || 'メールの取得に失敗しました');
            } finally {
                setMailLoading(false);
            }
        };

        fetchMailMessages();
    }, [userEmail]);

    const formatMemoDate = (value?: Timestamp | Date | null) => {
        if (!value) return '-';
        if (value instanceof Timestamp) return value.toDate().toLocaleString('ja-JP');
        if (value instanceof Date) return value.toLocaleString('ja-JP');
        return String(value);
    };

    const truncateMemoText = (text?: string | null, maxLength = 100) => {
        if (!text) return '-';
        if (text.length <= maxLength) return text;
        return `${text.slice(0, maxLength)}...`;
    };

    const mailPageSize = 5;
    const mailTotalPages = Math.max(1, Math.ceil(mailMessages.length / mailPageSize));
    const mailPageStart = (mailPage - 1) * mailPageSize;
    const mailPageMessages = mailMessages.slice(mailPageStart, mailPageStart + mailPageSize);

    useEffect(() => {
        setMailPage(1);
    }, [mailMessages.length]);

    useEffect(() => {
        const fetchAdminUsers = async () => {
            try {
                const adminSnapshot = await getDocs(query(collection(db, 'User'), where('Role', '==', 'admin')));
                const adminOptions = adminSnapshot.docs.map((userDoc) => {
                    const data = userDoc.data();
                    const displayName = data.display_name || data.display_Name || userDoc.id;
                    return {
                        id: userDoc.id,
                        displayName,
                    };
                });
                adminOptions.sort((a, b) => a.displayName.localeCompare(b.displayName, 'ja'));
                setAdminUsers(adminOptions);
            } catch (err) {
                console.error('Error fetching admin users:', err);
                setAdminUsers([]);
            }
        };

        fetchAdminUsers();
    }, []);

    const handleOpenMemoModal = () => {
        setMemoCreatedAt(new Date());
        setMemoText('');
        setMemoTaskStatus(null);
        setMemoTaskLimit('');
        setMemoAccountId('');
        setIsMemoModalOpen(true);
    };

    const resolveUserName = async (userRef?: DocumentReference | string | null) => {
        if (!userRef) return '';
        try {
            const resolvedRef = typeof userRef === 'string' ? doc(db, 'User', userRef) : userRef;
            const snapshot = await getDoc(resolvedRef);
            if (!snapshot.exists()) return '';
            const userData = snapshot.data();
            return userData.display_name || userData.display_Name || '';
        } catch (err) {
            console.error('Error fetching user name:', err);
            return '';
        }
    };

    const resolveShikakuName = async (orderRef?: DocumentReference) => {
        if (!orderRef) return '';
        try {
            const snapshot = await getDoc(orderRef);
            if (!snapshot.exists()) return '';
            const orderInfo = snapshot.data();
            const shikakuRef = orderInfo.shikakuId;
            if (shikakuRef?.path) {
                const shikakuSnapshot = await getDoc(shikakuRef);
                if (shikakuSnapshot.exists()) {
                    return shikakuSnapshot.data().ShikakuName || '';
                }
            }
            if (typeof shikakuRef === 'string') {
                const shikakuSnapshot = await getDoc(doc(db, 'Shikaku', shikakuRef));
                if (shikakuSnapshot.exists()) {
                    return shikakuSnapshot.data().ShikakuName || '';
                }
            }
            return '';
        } catch (err) {
            console.error('Error fetching shikaku name:', err);
            return '';
        }
    };

    const handleOpenMemoDetail = async (memo: MemoItem) => {
        const [createUserName, targetUserName, shikakuNameValue] = await Promise.all([
            resolveUserName(memo.CreateUser),
            resolveUserName(memo.TGTuser ?? undefined),
            resolveShikakuName(memo.TGTorder),
        ]);
        setMemoDetail({
            memo,
            createUserName,
            targetUserName,
            shikakuName: shikakuNameValue,
        });
        setMemoDetailText(memo.text ?? '');
        setIsMemoDetailEditing(false);
        setIsMemoDetailOpen(true);
    };

    useEffect(() => {
        const fetchCurrentStaff = async () => {
            if (!currentUserId) return;
            const staffRef = doc(db, 'User', currentUserId);
            const staffName = await resolveUserName(staffRef);
            setCurrentStaffName(staffName);
        };

        fetchCurrentStaff();
    }, [currentUserId]);

    const handleCreateMemo = async () => {
        if (!id || memoSaving) return;
        const trimmedText = memoText.trim();
        if (!trimmedText) return;
        if (!currentUserId) {
            alert('ログインユーザー情報が取得できませんでした');
            return;
        }
        if (memoTaskStatus === 'active') {
            if (!memoTaskLimit) {
                alert('タスク期日を入力してください');
                return;
            }
            if (!memoAccountId) {
                alert('担当者を選択してください');
                return;
            }
        }
        setMemoSaving(true);
        try {
            const createdAtValue = memoCreatedAt ?? new Date();
            const createUserId = currentUserId;
            const orderRef = doc(db, 'Order', id);
            const orderUserRef = orderData?.UserRef
                ? orderData.UserRef.path
                    ? orderData.UserRef
                    : doc(db, 'User', orderData.UserRef)
                : userId
                  ? doc(db, 'User', userId)
                  : null;
            const taskLimitDate = memoTaskLimit ? new Date(`${memoTaskLimit}T00:00:00`) : null;
            const shouldIncludeTaskDetails = memoTaskStatus === 'active' || memoTaskStatus === 'done';
            const memoPayload = {
                CreateAt: Timestamp.fromDate(createdAtValue),
                text: trimmedText,
                CreateUser: createUserId,
                TGTorder: orderRef,
                TGTuser: orderUserRef,
                task: memoTaskStatus ?? null,
                tasklimit: shouldIncludeTaskDetails && taskLimitDate ? Timestamp.fromDate(taskLimitDate) : null,
                account: shouldIncludeTaskDetails && memoAccountId ? doc(db, 'User', memoAccountId) : null,
            };
            const memoDoc = await addDoc(collection(db, 'Memo'), memoPayload);
            setMemos((prev) => [{ id: memoDoc.id, ...memoPayload }, ...prev]);
            setIsMemoModalOpen(false);
        } catch (err: any) {
            console.error('Error creating memo:', err);
            alert(err?.message || 'メモの作成に失敗しました');
        } finally {
            setMemoSaving(false);
        }
    };

    const handleUpdateMemoDetail = async () => {
        if (!memoDetail || memoDetailSaving) return;
        const trimmedText = memoDetailText.trim();
        if (!trimmedText) {
            alert('メモ内容を入力してください');
            return;
        }
        setMemoDetailSaving(true);
        try {
            await updateDoc(doc(db, 'Memo', memoDetail.memo.id), {
                text: trimmedText,
            });
            setMemos((prev) =>
                prev.map((memo) => (memo.id === memoDetail.memo.id ? { ...memo, text: trimmedText } : memo))
            );
            setMemoDetail((prev) =>
                prev
                    ? {
                          ...prev,
                          memo: {
                              ...prev.memo,
                              text: trimmedText,
                          },
                      }
                    : prev
            );
            setIsMemoDetailEditing(false);
        } catch (err: any) {
            console.error('Error updating memo:', err);
            alert(err?.message || 'メモの更新に失敗しました');
        } finally {
            setMemoDetailSaving(false);
        }
    };

    const canDeleteMemoByCurrentUser = (memo?: MemoItem | null) => {
        if (!memo || !currentUserId) return false;
        const creator = memo.CreateUser;
        const creatorId = typeof creator === 'string' ? creator : creator?.id;
        return creatorId === currentUserId;
    };

    const handleDeleteMemoDetail = async () => {
        if (!memoDetail || memoDetailSaving) return;
        if (!canDeleteMemoByCurrentUser(memoDetail.memo)) {
            alert('作成者本人のみ削除できます');
            return;
        }
        if (!window.confirm('このメモを削除しますか？')) return;

        setMemoDetailSaving(true);
        try {
            await deleteDoc(doc(db, 'Memo', memoDetail.memo.id));
            setMemos((prev) => prev.filter((memo) => memo.id !== memoDetail.memo.id));
            setIsMemoDetailEditing(false);
            setIsMemoDetailOpen(false);
            setMemoDetail(null);
        } catch (err: any) {
            console.error('Error deleting memo:', err);
            alert(err?.message || 'メモの削除に失敗しました');
        } finally {
            setMemoDetailSaving(false);
        }
    };

    const handleOpenReceiptModal = () => {
        const today = toDateInputValue(new Date());
        const createdAtValue = getOrderCreatedAtValue();
        const orderCreatedAt = createdAtValue ? toDateInputValue(createdAtValue) : today;
        setReceiptIssueDate(orderData?.receiptIssueDate || today);
        setReceiptApplicationDate(orderData?.receiptApplicationDate || orderCreatedAt);
        setReceiptCustomerName(orderData?.receiptCustomerName || userName || '');
        setReceiptRemark(orderData?.receiptRemark ?? 'ーー');
        setReceiptDownloadUrl(orderData?.receiptDownloadUrl || '');
        setIsReceiptModalOpen(true);
        void (async () => {
            if (receiptSealUrl && receiptSealImage) return;
            try {
                const sealRef = storageRef(storage, 'admin/角印 RW.png');
                const downloadUrl = await getDownloadURL(sealRef);
                setReceiptSealUrl(downloadUrl);
                if (!receiptSealImage) {
                    const response = await fetch(downloadUrl);
                    const blob = await response.blob();
                    const imageBitmap = await createImageBitmap(blob);
                    const canvas = document.createElement('canvas');
                    canvas.width = imageBitmap.width;
                    canvas.height = imageBitmap.height;
                    const context = canvas.getContext('2d');
                    if (!context) return;
                    context.drawImage(imageBitmap, 0, 0);
                    const jpegBlob = await new Promise<Blob>((resolve, reject) => {
                        canvas.toBlob((result) => {
                            if (result) {
                                resolve(result);
                            } else {
                                reject(new Error('領収書印影の変換に失敗しました'));
                            }
                        }, 'image/jpeg');
                    });
                    const buffer = await jpegBlob.arrayBuffer();
                    setReceiptSealImage({ data: new Uint8Array(buffer), width: imageBitmap.width, height: imageBitmap.height });
                }
            } catch (err) {
                console.error('Error loading receipt seal:', err);
            }
        })();
    };

    const handleCreateReceiptPdf = async () => {
        if (!id || receiptSaving) return;
        if (!receiptIssueDate || !receiptApplicationDate) {
            alert('申込日と発行日を入力してください');
            return;
        }
        setReceiptSaving(true);
        try {
            const pdfBlob = await buildReceiptPdfBlob();
            const fileName = `receipt-${id}-${Date.now()}.pdf`;
            const storagePath = `admin/receipts/${id}/${fileName}`;
            const storageFileRef = storageRef(storage, storagePath);
            await uploadBytes(storageFileRef, pdfBlob, { contentType: 'application/pdf' });
            const downloadUrl = await getDownloadURL(storageFileRef);
            await updateDoc(doc(db, 'Order', id), {
                receiptDownloadUrl: downloadUrl,
                receiptFileName: fileName,
                receiptStoragePath: storagePath,
                receiptIssueDate,
                receiptApplicationDate,
                receiptCustomerName,
                receiptRemark,
                receiptUpdatedAt: serverTimestamp(),
            });
            setOrderData((prev) =>
                prev
                    ? {
                          ...prev,
                          receiptDownloadUrl: downloadUrl,
                          receiptFileName: fileName,
                          receiptStoragePath: storagePath,
                          receiptIssueDate,
                          receiptApplicationDate,
                          receiptCustomerName,
                          receiptRemark,
                      }
                    : prev
            );
            setReceiptDownloadUrl(downloadUrl);
        } catch (err: any) {
            console.error('Error creating receipt PDF:', err);
            alert(err?.message || '領収書PDFの作成に失敗しました');
        } finally {
            setReceiptSaving(false);
        }
    };

    const normalizedData: NormalizedData | null = useMemo(() => {
        if (!orderData) return null;
        return normalizeValue(orderData);
    }, [orderData]);

    const shikakuName = useMemo(() => {
        if (!selectedShikakuId) return '';
        return shikakus.find((shikaku) => shikaku.id === selectedShikakuId)?.name || selectedShikakuId;
    }, [selectedShikakuId, shikakus]);

    const receiptTotalAmount = useMemo(() => getTotalAmountValue(), [orderData]);
    const receiptSubtotal = useMemo(() => getReceiptSubtotal(), [orderData]);
    const receiptTax = useMemo(() => getReceiptTax(), [orderData]);

    const editableKeys = new Set([
        'status',
        'trouble',
        'lost',
        'totalAmount',
        'paymentMethod',
        'orderNumber',
        'course',
        'diploma',
        'accessories',
        'expiresAt',
        'Hstatus',
        'testtype',
        'Tstatus',
        'HarvDate',
        'TarvDate',
        'trackingNumbers',
        'DtrackingNumber',
        'HsndDate',
        'Dstatus',
        'TsndDate',
        'DsendDate',
    ]);

    const renderEditableField = (key: keyof OrderEditForm) => {
        switch (key) {
            case 'status':
                return (
                    <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={editForm.status}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                        disabled={saving}
                    >
                        <option value="">選択してください</option>
                        <option value="active">有効</option>
                        <option value="cancell">キャンセル</option>
                    </select>
                );
            case 'trouble':
                return (
                    <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={editForm.trouble ? 'true' : 'false'}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, trouble: e.target.value === 'true' }))}
                        disabled={saving}
                    >
                        <option value="false">--</option>
                        <option value="true">要注意</option>
                    </select>
                );
            case 'lost':
                return (
                    <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={editForm.lost ? 'true' : 'false'}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, lost: e.target.value === 'true' }))}
                        disabled={saving}
                    >
                        <option value="false">--</option>
                        <option value="true">貸倒</option>
                    </select>
                );
            case 'Hstatus':
                return (
                    <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={editForm.Hstatus}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, Hstatus: e.target.value }))}
                        disabled={saving}
                    >
                        <option value="">未完了</option>
                        <option value="done">完了</option>
                    </select>
                );
            case 'testtype':
                return (
                    <div className="space-y-1">
                        <select
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:text-gray-400"
                            value={editForm.testtype}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, testtype: e.target.value }))}
                            disabled={saving || !canEditTesttype}
                        >
                            <option value="">--</option>
                            <option value="paper">用紙</option>
                            <option value="online">オンライン</option>
                        </select>
                        {!canEditTesttype && (
                            <p className="text-xs text-gray-500">Hstatusがarriveまたはdoneの場合のみ変更できます。</p>
                        )}
                    </div>
                );
            case 'Tstatus':
                return (
                    <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={editForm.Tstatus}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, Tstatus: e.target.value }))}
                        disabled={saving}
                    >
                        <option value="">未完了</option>
                        <option value="done">完了</option>
                    </select>
                );
            case 'Dstatus':
                return (
                    <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={editForm.Dstatus}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, Dstatus: e.target.value }))}
                        disabled={saving}
                    >
                        <option value="">未設定</option>
                        <option value="ready">準備</option>
                        <option value="done">完了</option>
                        <option value="stop">停止</option>
                    </select>
                );
            case 'HarvDate':
                return (
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            value={editForm.HarvDate}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, HarvDate: e.target.value }))}
                            disabled={saving}
                        />
                        <button
                            type="button"
                            className="text-xs text-gray-500 hover:text-gray-700"
                            onClick={() => setEditForm((prev) => ({ ...prev, HarvDate: '' }))}
                            disabled={saving}
                        >
                            クリア
                        </button>
                    </div>
                );
            case 'TarvDate':
                return (
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            value={editForm.TarvDate}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, TarvDate: e.target.value }))}
                            disabled={saving}
                        />
                        <button
                            type="button"
                            className="text-xs text-gray-500 hover:text-gray-700"
                            onClick={() => setEditForm((prev) => ({ ...prev, TarvDate: '' }))}
                            disabled={saving}
                        >
                            クリア
                        </button>
                    </div>
                );
            case 'HsndDate':
                return (
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            value={editForm.HsndDate}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, HsndDate: e.target.value }))}
                            disabled={saving}
                        />
                        <button
                            type="button"
                            className="text-xs text-gray-500 hover:text-gray-700"
                            onClick={() => setEditForm((prev) => ({ ...prev, HsndDate: '' }))}
                            disabled={saving}
                        >
                            クリア
                        </button>
                    </div>
                );
            case 'TsndDate':
                return (
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            value={editForm.TsndDate}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, TsndDate: e.target.value }))}
                            disabled={saving}
                        />
                        <button
                            type="button"
                            className="text-xs text-gray-500 hover:text-gray-700"
                            onClick={() => setEditForm((prev) => ({ ...prev, TsndDate: '' }))}
                            disabled={saving}
                        >
                            クリア
                        </button>
                    </div>
                );
            case 'DsendDate':
                return (
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            value={editForm.DsendDate}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, DsendDate: e.target.value }))}
                            disabled={saving}
                        />
                        <button
                            type="button"
                            className="text-xs text-gray-500 hover:text-gray-700"
                            onClick={() => setEditForm((prev) => ({ ...prev, DsendDate: '' }))}
                            disabled={saving}
                        >
                            クリア
                        </button>
                    </div>
                );
            case 'totalAmount':
                return (
                    <input
                        type="number"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={editForm.totalAmount}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, totalAmount: e.target.value }))}
                        disabled={saving}
                    />
                );
            case 'course':
                return (
                    <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={editForm.course}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, course: e.target.value }))}
                        disabled={saving}
                    >
                        <option value="">選択してください</option>
                        <option value="通信講座＋試験">通信講座＋試験</option>
                        <option value="試験のみ">試験のみ</option>
                    </select>
                );
            case 'diploma':
                return (
                    <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={editForm.diploma}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, diploma: e.target.value }))}
                        disabled={saving}
                    >
                        <option value="">選択してください</option>
                        <option value="通常版">通常版</option>
                        <option value="証書ケース">証書ケース</option>
                        <option value="アクリルスタンド">アクリルスタンド</option>
                    </select>
                );
            case 'paymentMethod':
                return (
                    <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={editForm.paymentMethod}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                        disabled={saving}
                    >
                        <option value="">選択してください</option>
                        <option value="クレジット">クレジット</option>
                        <option value="スマホ決済">スマホ決済</option>
                        <option value="後払い">後払い</option>
                        <option value="後払い分割">後払い分割</option>
                        <option value="コンビニ決済">コンビニ決済</option>
                    </select>
                );
            case 'expiresAt':
                return (
                    <input
                        type="datetime-local"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={editForm.expiresAt}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, expiresAt: e.target.value }))}
                        disabled={saving}
                    />
                );
            case 'trackingNumbers':
                return (
                    <textarea
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        rows={3}
                        value={editForm.trackingNumbers}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, trackingNumbers: e.target.value }))}
                        placeholder="追跡番号を改行またはカンマ区切りで入力"
                        disabled={saving}
                    />
                );
            default:
                return (
                    <input
                        type="text"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={editForm[key] ?? ''}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, [key]: e.target.value }))}
                        disabled={saving}
                    />
                );
        }
    };

    const renderValue = (key: string) => {
        if (key === 'UserRef') {
            if (userId && userName) {
                return (
                    <div className="flex items-center gap-2">
                        <Link to={`/admin/users/${userId}`} className="text-emerald-600 font-semibold hover:underline">
                            {userName}
                        </Link>
                        {orderData?.trouble && <span className="text-red-500 font-semibold">！</span>}
                    </div>
                );
            }
            if (userId) {
                return 'ユーザー情報を取得できませんでした';
            }
            return 'ユーザー情報なし';
        }

        if (key === 'trouble' && !isEditing) {
            if (orderData?.trouble) {
                return <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-semibold text-white">要注意</span>;
            }
            return <span className="text-gray-800">--</span>;
        }

        if (key === 'lost' && !isEditing) {
            if (orderData?.lost) {
                return <span className="text-gray-800">貸倒</span>;
            }
            return <span className="text-gray-800">--</span>;
        }

        if (key === 'trackingNumbers' && !isEditing) {
            const numbers = Array.isArray(orderData?.trackingNumbers)
                ? orderData.trackingNumbers.map((value: unknown) => String(value)).filter((value: string) => value.trim() !== '')
                : [];
            return <span className="text-gray-800">{numbers.length ? numbers.join('、') : '-'}</span>;
        }

        if (key === 'testtype' && !isEditing) {
            if (orderData?.testtype === 'paper') {
                return <span className="text-gray-800">用紙</span>;
            }
            if (orderData?.testtype === 'online') {
                return <span className="text-gray-800">オンライン</span>;
            }
            return <span className="text-gray-800">--</span>;
        }

        if (key === 'shikakuId') {
            if (!isEditing) {
                return <span className="text-gray-800">{shikakuName || '-'}</span>;
            }
            return (
                <div className="flex flex-col gap-3">
                    <select
                        className="border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={selectedShikakuId}
                        onChange={(e) => setSelectedShikakuId(e.target.value)}
                        disabled={saving || loading}
                    >
                        <option value="" disabled>
                            資格を選択してください
                        </option>
                        {shikakus.map((shikaku) => (
                            <option key={shikaku.id} value={shikaku.id}>
                                {shikaku.name}
                            </option>
                        ))}
                    </select>
                </div>
            );
        }

        if (key === 'status' && !isEditing) {
            const statusValue = orderData?.status ? String(orderData.status) : '';
            const statusLabel =
                statusValue === 'active' ? '有効' : statusValue === 'cancell' ? 'キャンセル' : statusValue || '-';
            return <span className="text-gray-800">{statusLabel}</span>;
        }

        if (isEditing && editableKeys.has(key)) {
            return renderEditableField(key as keyof OrderEditForm);
        }

        const normalizedValue = normalizedData ? normalizedData[key] : orderData?.[key];

        if (typeof normalizedValue === 'object') {
            return <pre className="whitespace-pre-wrap text-xs text-gray-700">{JSON.stringify(normalizedValue, null, 2)}</pre>;
        }

        return <span className="text-gray-800">{String(normalizedValue ?? '')}</span>;
    };

    const leftColumnKeys = useMemo(
        () => [
            'status',
            'trouble',
            'lost',
            'UserRef',
            'shikakuId',
            'course',
            'diploma',
            'accessories',
            'totalAmount',
            'trackingNumbers',
            'DtrackingNumber',
            'Dstatus',
            'HsndDate',
            'TsndDate',
            'DsendDate',
        ],
        []
    );
    const rightColumnKeys = useMemo(
        () => [
            'postalCode',
            'address',
            'paymentMethod',
            'paymentStatus',
            'createdAt',
            'orderNumber',
            'expiresAt',
        ],
        []
    );

    const otherKeys = useMemo(() => {
        if (!orderData) return [] as string[];
        const displayedKeys = new Set([...leftColumnKeys, ...rightColumnKeys]);
        const keys = Array.from(new Set([...Object.keys(orderData), 'testtype'])).filter((key) => !displayedKeys.has(key));
        keys.sort((a, b) => {
            if (a === 'Hstatus' && b === 'testtype') return -1;
            if (a === 'testtype' && b === 'Hstatus') return 1;
            return 0;
        });
        return keys;
    }, [leftColumnKeys, rightColumnKeys, orderData]);

    const getStatusBadgeClass = (status?: string | null, hasArrive?: boolean) => {
        if (!status) return 'bg-gray-200 text-gray-600';
        if (status === 'done') return 'bg-emerald-500 text-white';
        if (hasArrive && status === 'arrive') return 'bg-orange-400 text-white';
        return 'bg-gray-200 text-gray-600';
    };

    const getDiplomaBadgeClass = (status?: string | null) => {
        if (!status) return 'bg-gray-200 text-gray-600';
        if (status === 'done') return 'bg-emerald-500 text-white';
        if (status === 'ready') return 'bg-orange-400 text-white';
        if (status === 'stop') return 'bg-red-500 text-white';
        return 'bg-gray-200 text-gray-600';
    };

    const fieldItem = (key: string, compact?: boolean) => (
        <div key={key} className={`${compact ? 'py-2' : 'py-3 border-b border-gray-100 last:border-b-0'}`}>
            <div className={`text-xs text-gray-500 ${compact ? 'mb-0.5' : 'mb-1'}`}>
                {FIELD_LABELS[key as keyof typeof FIELD_LABELS] || key}
            </div>
            <div className={`font-medium ${compact ? 'text-sm text-gray-900' : 'text-sm'}`}>{renderValue(key)}</div>
        </div>
    );

    return (
        <div className="p-6 space-y-8">
            <div className="text-sm text-gray-600 flex items-center gap-2">
                <Link to={`/admin/orders${listQueryString}`} className="text-emerald-600 hover:underline">
                    一覧
                </Link>
                <span className="text-gray-400">&gt;</span>
                <span className="text-gray-800 font-medium">{orderData?.orderNumber || '注文詳細'}</span>
            </div>

            {!loading && !error && (
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-lg font-semibold text-gray-800">Order詳細</div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => prevOrderId && navigate(getOrderDetailLink(prevOrderId))}
                            disabled={!prevOrderId || navigationLoading}
                            className="px-3 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                            前のページ
                        </button>
                        <button
                            type="button"
                            onClick={() => nextOrderId && navigate(getOrderDetailLink(nextOrderId))}
                            disabled={!nextOrderId || navigationLoading}
                            className="px-3 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                            次のページ
                        </button>
                        {isEditing ? (
                            <>
                                <button
                                    onClick={handleCancel}
                                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                                    disabled={saving}
                                >
                                    キャンセル
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="px-4 py-2 bg-emerald-500 text-white rounded-lg shadow-sm hover:bg-emerald-600 disabled:opacity-50"
                                >
                                    {saving ? '保存中...' : '変更を保存'}
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="px-4 py-2 bg-emerald-500 text-white rounded-lg shadow-sm hover:bg-emerald-600"
                            >
                                編集
                            </button>
                        )}
                    </div>
                </div>
            )}

            {loading && <div>読み込み中...</div>}
            {error && <div className="text-red-600 mb-4">{error}</div>}

            {!loading && !error && normalizedData && orderData && (
                <>
                    <div className="space-y-6">
                        <div className="flex items-center gap-4">
                            <h2 className="text-lg font-semibold text-gray-800">Action</h2>
                            <div className="border-t border-gray-300 flex-1" />
                        </div>
                        <div className="flex flex-wrap gap-6">
                            {[
                                { label: '発送用紙', icon: '📦', to: id ? `/admin/orders/${id}/shipping` : '' },
                                { label: '添削課題', icon: '📝', to: id ? `/admin/orders/${id}/task` : '' },
                                { label: '本試験', icon: '❗', to: id ? `/admin/orders/${id}/exam` : '' },
                                { label: 'メール作成', icon: '✉️', to: id ? `/admin/mail/new?orderId=${id}` : '' },
                                { label: '領収書発行', icon: '🧾', onClick: handleOpenReceiptModal },
                            ].map((action) =>
                                action.to ? (
                                    <Link
                                        key={action.label}
                                        to={action.to}
                                        className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white px-6 py-4 text-gray-700 shadow-sm hover:border-emerald-200 hover:text-emerald-600"
                                    >
                                        <span className="text-2xl">{action.icon}</span>
                                        <span className="text-sm font-semibold">{action.label}</span>
                                    </Link>
                                ) : (
                                    <button
                                        key={action.label}
                                        className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white px-6 py-4 text-gray-700 shadow-sm hover:border-emerald-200 hover:text-emerald-600"
                                        type="button"
                                        onClick={action.onClick}
                                    >
                                        <span className="text-2xl">{action.icon}</span>
                                        <span className="text-sm font-semibold">{action.label}</span>
                                    </button>
                                )
                            )}
                        </div>
                    </div>

                    <div className="grid gap-6 lg:grid-cols-3">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-col gap-4 lg:col-span-1">
                            <div className="space-y-2">
                                {leftColumnKeys.map((key) => fieldItem(key, true))}
                            </div>
                        </div>

                        <div className="lg:col-span-2 space-y-5">
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                                <div className="grid md:grid-cols-4 gap-4">
                                    {[
                                        { key: 'TarvDate', label: '到着' },
                                        { key: 'TsnDDate', label: '送付日' },
                                        { key: 'HarvDate', label: '到着' },
                                        { key: 'HsnDDate', label: '送付日' },
                                    ].map((item, index) => (
                                        <div key={`${item.key}-${index}`} className="space-y-1">
                                            <div className="text-[15px] text-gray-500">{item.label}</div>
                                            <div className="text-sm font-medium text-gray-800">{renderValue(item.key)}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-5">
                                    <div className="relative flex items-center justify-between">
                                        <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-gray-300" />
                                        {[
                                            {
                                                label: '発送',
                                                status: orderData?.Pstatus,
                                                hasArrive: false,
                                            },
                                            {
                                                label: '添削課題',
                                                status: orderData?.Tstatus,
                                                hasArrive: true,
                                            },
                                            {
                                                label: '本試験',
                                                status: orderData?.Hstatus,
                                                hasArrive: true,
                                            },
                                            {
                                                label: '認定証発送',
                                                status: orderData?.Dstatus,
                                                hasArrive: false,
                                            },
                                        ].map((item) => {
                                            const isShippingAction = item.label === '発送';
                                            const isTaskAction = item.label === '添削課題';
                                            const isExamAction = item.label === '本試験';
                                            const isDiplomaAction = item.label === '認定証発送';
                                            const className = `inline-flex items-center rounded-full px-4 py-1 text-m font-semibold ${getStatusBadgeClass(
                                                item.status,
                                                item.hasArrive
                                            )} ${isShippingAction ? 'transition hover:opacity-80' : ''}`;
                                            const diplomaClassName = `inline-flex items-center rounded-full px-4 py-1 text-m font-semibold ${getDiplomaBadgeClass(
                                                item.status
                                            )} transition hover:opacity-80`;
                                            return (
                                                <div key={item.label} className="relative z-10">
                                                    {isShippingAction ? (
                                                        <button
                                                            type="button"
                                                            className={className}
                                                            onClick={() => setIsShippingModalOpen(true)}
                                                        >
                                                            {item.label}
                                                        </button>
                                                    ) : isTaskAction ? (
                                                        <button
                                                            type="button"
                                                            className={`${className} transition hover:opacity-80`}
                                                            onClick={() => setIsTaskModalOpen(true)}
                                                        >
                                                            {item.label}
                                                        </button>
                                                    ) : isExamAction ? (
                                                        <button
                                                            type="button"
                                                            className={`${className} transition hover:opacity-80`}
                                                            onClick={() => setIsExamModalOpen(true)}
                                                        >
                                                            {item.label}
                                                        </button>
                                                    ) : isDiplomaAction ? (
                                                        <button
                                                            type="button"
                                                            className={diplomaClassName}
                                                            onClick={() => {
                                                                if (orderData?.Dstatus === 'stop') {
                                                                    alert('停止中です');
                                                                    return;
                                                                }
                                                                setIsDiplomaModalOpen(true);
                                                            }}
                                                        >
                                                            {item.label}
                                                        </button>
                                                    ) : (
                                                        <span className={className}>{item.label}</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-2">
                                    <div className="text-[15px] text-gray-500">発送先住所</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {userPostalCode || renderValue('postalCode')}
                                    </div>
                                    <div className="text-[15px] font-medium text-gray-800">{userAddress || renderValue('address')}</div>
                                </div>
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className="text-[15px] text-gray-500">{FIELD_LABELS.paymentMethod}</div>
                                        <div className="text-[15px] text-gray-500">{FIELD_LABELS.paymentStatus}</div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="text-[15px] font-medium text-gray-800">{renderValue('paymentMethod')}</div>
                                        <div className="text-[15px] font-medium text-gray-800">{renderValue('paymentStatus')}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-1 gap-4">
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-2">
                                    <div className="text-[15px] text-gray-500">{FIELD_LABELS.createdAt}</div>
                                    <div className="text-[18px] font-medium text-gray-800">{renderValue('createdAt')}</div>
                                    <div className="text-[15px] text-gray-500">{FIELD_LABELS.orderNumber}</div>
                                    <div className="text-[18px] font-medium text-gray-800">{renderValue('orderNumber')}</div>
                                    <div className="text-[15px] text-gray-500">{FIELD_LABELS.expiresAt}</div>
                                    <div className="text-[15px] font-medium text-gray-800">{renderValue('expiresAt')}</div>
                                </div>
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                                    <div
                                        className={`flex items-center justify-between rounded-full bg-emerald-400/90 px-4 py-2 text-xs font-semibold text-white ${
                                            isEditing ? 'grid grid-cols-6 gap-3' : 'grid grid-cols-5 gap-3'
                                        }`}
                                    >
                                        <span>入金期日</span>
                                        <span>予定金額</span>
                                        <span>入金実績</span>
                                        <span>チェック</span>
                                        <span>入金日</span>
                                        {isEditing && <span>操作</span>}
                                    </div>
                                    <div className="mt-3 space-y-2 text-sm text-gray-800">
                                        {isEditing && (
                                            <div className="grid grid-cols-6 gap-3 rounded-lg border border-dashed border-emerald-200 bg-emerald-50/50 px-3 py-3 text-xs">
                                                <input
                                                    type="date"
                                                    className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                    value={newPaymentForm.dueDate}
                                                    onChange={(event) =>
                                                        setNewPaymentForm((prev) => ({ ...prev, dueDate: event.target.value }))
                                                    }
                                                />
                                                <input
                                                    type="number"
                                                    className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                    value={newPaymentForm.amount}
                                                    onChange={(event) =>
                                                        setNewPaymentForm((prev) => ({ ...prev, amount: event.target.value }))
                                                    }
                                                    min="0"
                                                />
                                                <input
                                                    type="number"
                                                    className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                    value={newPaymentForm.deposit}
                                                    onChange={(event) =>
                                                        setNewPaymentForm((prev) => ({ ...prev, deposit: event.target.value }))
                                                    }
                                                    min="0"
                                                />
                                                <label className="flex items-center justify-center gap-2 text-xs text-gray-600">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                                                        checked={newPaymentForm.paycheck}
                                                        onChange={(event) =>
                                                            setNewPaymentForm((prev) => ({
                                                                ...prev,
                                                                paycheck: event.target.checked,
                                                            }))
                                                        }
                                                    />
                                                    完了
                                                </label>
                                                <input
                                                    type="date"
                                                    className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                    value={newPaymentForm.paidAt}
                                                    onChange={(event) =>
                                                        setNewPaymentForm((prev) => ({ ...prev, paidAt: event.target.value }))
                                                    }
                                                />
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={handleAddPayment}
                                                        className="rounded-md bg-emerald-500 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-600"
                                                    >
                                                        追加
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={resetNewPaymentForm}
                                                        className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                                                    >
                                                        クリア
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {paymentsLoading && <div className="text-xs text-gray-500">入金情報を読み込み中...</div>}
                                        {!paymentsLoading && paymentsError && (
                                            <div className="text-xs text-red-500">{paymentsError}</div>
                                        )}
                                        {!paymentsLoading && !paymentsError && payments.length === 0 && (
                                            <div className="text-xs text-gray-500">入金情報はまだ登録されていません。</div>
                                        )}
                                        {!paymentsLoading &&
                                            !paymentsError &&
                                            payments.map((payment) => (
                                                <div
                                                    key={payment.id}
                                                    className={`grid gap-3 rounded-lg border border-gray-100 px-3 py-2 ${
                                                        isEditing ? 'grid-cols-6' : 'grid-cols-5'
                                                    }`}
                                                >
                                                    {isEditing ? (
                                                        <>
                                                            <input
                                                                type="date"
                                                                className="w-full rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                                value={paymentEditById.get(payment.id)?.dueDate ?? ''}
                                                                onChange={(event) =>
                                                                    setPaymentEdits((prev) =>
                                                                        prev.map((edit) =>
                                                                            edit.id === payment.id
                                                                                ? { ...edit, dueDate: event.target.value }
                                                                                : edit
                                                                        )
                                                                    )
                                                                }
                                                                disabled={saving}
                                                            />
                                                            <input
                                                                type="number"
                                                                className="w-full rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                                value={paymentEditById.get(payment.id)?.amount ?? ''}
                                                                onChange={(event) =>
                                                                    setPaymentEdits((prev) =>
                                                                        prev.map((edit) =>
                                                                            edit.id === payment.id
                                                                                ? { ...edit, amount: event.target.value }
                                                                                : edit
                                                                        )
                                                                    )
                                                                }
                                                                disabled={saving}
                                                            />
                                                            <input
                                                                type="number"
                                                                className="w-full rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                                value={paymentEditById.get(payment.id)?.deposit ?? ''}
                                                                onChange={(event) =>
                                                                    setPaymentEdits((prev) =>
                                                                        prev.map((edit) =>
                                                                            edit.id === payment.id
                                                                                ? { ...edit, deposit: event.target.value }
                                                                                : edit
                                                                        )
                                                                    )
                                                                }
                                                                disabled={saving}
                                                            />
                                                            <label className="flex items-center justify-center gap-2 text-xs text-gray-600">
                                                                <input
                                                                    type="checkbox"
                                                                    className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                                                                    checked={paymentEditById.get(payment.id)?.paycheck ?? false}
                                                                    onChange={(event) =>
                                                                        setPaymentEdits((prev) =>
                                                                            prev.map((edit) =>
                                                                                edit.id === payment.id
                                                                                    ? { ...edit, paycheck: event.target.checked }
                                                                                    : edit
                                                                            )
                                                                        )
                                                                    }
                                                                    disabled={saving}
                                                                />
                                                                完了
                                                            </label>
                                                            <input
                                                                type="date"
                                                                className="w-full rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                                value={paymentEditById.get(payment.id)?.paidAt ?? ''}
                                                                onChange={(event) =>
                                                                    setPaymentEdits((prev) =>
                                                                        prev.map((edit) =>
                                                                            edit.id === payment.id
                                                                                ? { ...edit, paidAt: event.target.value }
                                                                                : edit
                                                                        )
                                                                    )
                                                                }
                                                                disabled={saving}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDeletePayment(payment.id)}
                                                                className="rounded-md bg-red-500 px-2 py-1 text-xs font-semibold text-white hover:bg-red-600"
                                                                disabled={saving}
                                                            >
                                                                削除
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="text-xs font-medium text-gray-700">
                                                                {formatDateValue(payment.dueDate)}
                                                            </div>
                                                            <div className="text-xs font-semibold text-gray-900">
                                                                {typeof payment.amount === 'number'
                                                                    ? payment.amount.toLocaleString('ja-JP')
                                                                    : '-'}
                                                            </div>
                                                            <div className="text-xs font-medium text-gray-700">
                                                                {typeof payment.deposit === 'number'
                                                                    ? payment.deposit.toLocaleString('ja-JP')
                                                                    : '-'}
                                                            </div>
                                                            <div className="text-xs font-medium text-gray-700">
                                                                {payment.paycheck ? '完了' : '-'}
                                                            </div>
                                                            <div className="text-xs font-medium text-gray-700">
                                                                {formatDateValue(payment.paidAt ?? payment.paymentDate)}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                    </div>
                                    {!paymentsLoading && !paymentsError && payments.length > 0 && (
                                        <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-700">
                                            <div className="flex flex-wrap items-center gap-4">
                                                <span>
                                                    予定合計: {paymentTotals.amountTotal.toLocaleString('ja-JP')}
                                                </span>
                                                <span>
                                                    入金合計: {paymentTotals.depositTotal.toLocaleString('ja-JP')}
                                                </span>
                                                <span>
                                                    差額: {paymentTotals.difference.toLocaleString('ja-JP')}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-6">
                                        <h4 className="text-sm font-semibold text-gray-700">入金履歴（bankdata）</h4>
                                        {bankdataLoading && <div className="mt-2 text-xs text-gray-500">bankdataを読み込み中...</div>}
                                        {!bankdataLoading && bankdataError && (
                                            <div className="mt-2 text-xs text-red-500">{bankdataError}</div>
                                        )}
                                        {!bankdataLoading && !bankdataError && bankdataItems.length === 0 && (
                                            <div className="mt-2 text-xs text-gray-500">該当するbankdataがありません。</div>
                                        )}
                                        {!bankdataLoading && !bankdataError && bankdataItems.length > 0 && (
                                            <div className="mt-2 overflow-x-auto rounded-lg border border-gray-100">
                                                <table className="min-w-full text-xs text-gray-700">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-3 py-2 text-left font-semibold">入金日</th>
                                                            <th className="px-3 py-2 text-right font-semibold">入金額</th>
                                                            <th className="px-3 py-2 text-left font-semibold">振込名義</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100 bg-white">
                                                        {bankdataItems.map((item) => (
                                                            <tr key={item.id}>
                                                                <td className="px-3 py-2">{formatDateValue(item.depositdate)}</td>
                                                                <td className="px-3 py-2 text-right">
                                                                    {typeof item.incash === 'number'
                                                                        ? item.incash.toLocaleString('ja-JP')
                                                                        : '-'}
                                                                </td>
                                                                <td className="px-3 py-2">{item.name || '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                        <div className="border-b border-gray-200 flex flex-wrap items-center justify-between gap-3 px-6">
                            <div className="flex items-center">
                                <button
                                    className={`py-3 px-4 font-semibold border-b-2 ${activeTab === 'mail' ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent'}`}
                                    onClick={() => setActiveTab('mail')}
                                >
                                    メール
                                </button>
                                <button
                                    className={`py-3 px-4 font-semibold border-b-2 ${activeTab === 'memo' ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent'}`}
                                    onClick={() => setActiveTab('memo')}
                                >
                                    メモ
                                </button>
                            </div>
                            {activeTab === 'memo' && (
                                <button
                                    type="button"
                                    className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600"
                                    onClick={handleOpenMemoModal}
                                >
                                    メモを作成
                                </button>
                            )}
                        </div>
                        {activeTab === 'mail' ? (
                            <div className="p-6">
                                {mailLoading && <div className="text-sm text-gray-500">メールを読み込み中...</div>}
                                {mailError && <div className="text-sm text-red-500">{mailError}</div>}
                                {!mailLoading && !mailError && mailMessages.length === 0 && (
                                    <div className="text-sm text-gray-500">メールはまだありません。</div>
                                )}
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-gray-700 bg-green-100">
                                                <th className="py-3 px-3 font-semibold">date</th>
                                                <th className="py-3 px-3 font-semibold">カテゴリ</th>
                                                <th className="py-3 px-3 font-semibold">タイトル</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {mailPageMessages.map((message) => {
                                                const isOutgoing = message.direction === 'out';
                                                const category = isOutgoing ? '送信' : '受信';
                                                const color = isOutgoing ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700';
                                                return (
                                                    <tr
                                                        key={message.id}
                                                        className="text-gray-800 cursor-pointer hover:bg-gray-50"
                                                        onClick={() => setSelectedMailMessage(message)}
                                                    >
                                                        <td className="py-3 px-3 align-top">{formatMemoDate(message.createdAt)}</td>
                                                        <td className="py-3 px-3 align-top">
                                                            <span
                                                                className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${color}`}
                                                            >
                                                                {category}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-3 align-top">{message.subject || '-'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                {mailMessages.length > mailPageSize && (
                                    <div className="mt-4 flex items-center justify-center gap-2 text-sm">
                                        <button
                                            type="button"
                                            className="rounded border border-gray-300 px-3 py-1 text-gray-600 disabled:opacity-50"
                                            onClick={() => setMailPage((prev) => Math.max(1, prev - 1))}
                                            disabled={mailPage === 1}
                                        >
                                            前へ
                                        </button>
                                        <span className="text-gray-600">
                                            {mailPage} / {mailTotalPages}
                                        </span>
                                        <button
                                            type="button"
                                            className="rounded border border-gray-300 px-3 py-1 text-gray-600 disabled:opacity-50"
                                            onClick={() => setMailPage((prev) => Math.min(mailTotalPages, prev + 1))}
                                            disabled={mailPage === mailTotalPages}
                                        >
                                            次へ
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="p-6 space-y-4">
                                {memoLoading && <div className="text-sm text-gray-500">メモを読み込み中...</div>}
                                {memoError && <div className="text-sm text-red-500">{memoError}</div>}
                                {!memoLoading && !memoError && memos.length === 0 && (
                                    <div className="text-sm text-gray-500">メモはまだありません。</div>
                                )}
                                {!memoLoading && !memoError && memos.length > 0 && (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="text-left text-gray-700 bg-green-100">
                                                    <th className="py-3 px-3 font-semibold">作成日時</th>
                                                    <th className="py-3 px-3 font-semibold">内容</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {memos.map((memo) => (
                                                    <tr key={memo.id} className="text-gray-800">
                                                        <td className="py-3 px-3 align-top">{formatMemoDate(memo.CreateAt)}</td>
                                                        <td className="py-3 px-3 align-top">
                                                            <button
                                                                type="button"
                                                                className="text-emerald-600 hover:underline text-left"
                                                                onClick={() => handleOpenMemoDetail(memo)}
                                                            >
                                                                {truncateMemoText(memo.text)}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {otherKeys.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <h3 className="text-base font-semibold text-gray-700 mb-4">その他のフィールド</h3>
                            <div className="divide-y divide-gray-100">
                                {otherKeys.map((key) => (
                                    <div key={key} className="py-3">
                                        <div className="text-xs text-gray-500 mb-1">{FIELD_LABELS[key as keyof typeof FIELD_LABELS] || key}</div>
                                        <div className="text-sm font-medium">{renderValue(key)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {isShippingModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-lg">
                        <div className="text-center text-lg font-semibold text-gray-800">商品発送</div>
                        <div className="mt-6 space-y-4">
                            <div className="text-sm text-gray-600 text-center">追跡番号を入力してください</div>
                            <div className="flex items-end gap-3">
                                <input
                                    type="text"
                                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                    value={trackingInput}
                                    onChange={(e) => setTrackingInput(e.target.value)}
                                    placeholder="追跡番号を入力"
                                />
                                <button
                                    type="button"
                                    className="text-sm font-semibold text-gray-700 hover:text-emerald-600"
                                    onClick={handleAddTrackingNumber}
                                >
                                    追加
                                </button>
                            </div>
                            {trackingNumbers.length > 0 && (
                                <div className="space-y-2">
                                    {trackingNumbers.map((number, index) => (
                                        <div
                                            key={`${number}-${index}`}
                                            className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700"
                                        >
                                            <span>{number}</span>
                                            <button
                                                type="button"
                                                className="text-xs font-semibold text-gray-400 hover:text-red-500"
                                                onClick={() => handleRemoveTrackingNumber(index)}
                                            >
                                                削除
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="mt-6 space-y-3">
                            <button
                                type="button"
                                onClick={handleShipOrder}
                                disabled={shippingSaving || (trackingNumbers.length === 0 && trackingInput.trim() === '')}
                                className="w-full rounded-lg bg-black py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                            >
                                {shippingSaving ? '発送中...' : '発送＆通知'}
                            </button>
                            <button
                                type="button"
                                className="w-full rounded-lg border border-gray-200 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                onClick={() => setIsShippingModalOpen(false)}
                                disabled={shippingSaving}
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isTaskModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-lg">
                        <div className="text-center text-lg font-semibold text-gray-800">添削課題</div>
                        <div className="mt-6 space-y-4">
                            {orderData?.Tstatus === 'done' ? (
                                <div className="text-sm text-gray-600 text-center">発送済みです</div>
                            ) : orderData?.Tstatus === 'arrive' ? (
                                <>
                                    <div className="text-sm text-gray-600 text-center">追跡番号を入力してください</div>
                                    <input
                                        type="text"
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                        value={taskTrackingInput}
                                        onChange={(e) => setTaskTrackingInput(e.target.value)}
                                        placeholder="追跡番号を入力"
                                    />
                                    <label className="flex items-center justify-center gap-2 text-xs text-gray-600">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                                            checked={skipTaskMail}
                                            onChange={(event) => setSkipTaskMail(event.target.checked)}
                                        />
                                        メール送信しない
                                    </label>
                                </>
                            ) : (
                                <div className="text-sm text-gray-600 text-center">到着確認を行ってください</div>
                            )}
                        </div>
                        <div className="mt-6 space-y-3">
                            {orderData?.Tstatus === 'done' ? null : orderData?.Tstatus === 'arrive' ? (
                                <button
                                    type="button"
                                    onClick={handleTaskShip}
                                    disabled={taskSaving || taskTrackingInput.trim() === ''}
                                    className="w-full rounded-lg bg-black py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                                >
                                    {taskSaving ? '発送中...' : '発送＆通知'}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleTaskArrive}
                                    disabled={taskSaving}
                                    className="w-full rounded-lg bg-black py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                                >
                                    {taskSaving ? '更新中...' : '到着確認'}
                                </button>
                            )}
                            <button
                                type="button"
                                className="w-full rounded-lg border border-gray-200 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                onClick={() => setIsTaskModalOpen(false)}
                                disabled={taskSaving}
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isExamModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-lg">
                        <div className="text-center text-lg font-semibold text-gray-800">本試験</div>
                        <div className="mt-6 space-y-4">
                            {orderData?.Hstatus === 'done' ? (
                                <div className="text-sm text-gray-600 text-center">採点完了済みです</div>
                            ) : orderData?.Hstatus === 'arrive' ? (
                                <div className="text-sm text-gray-600 text-center">採点完了を行ってください</div>
                            ) : (
                                <div className="text-sm text-gray-600 text-center">到着確認を行ってください</div>
                            )}
                        </div>
                        <div className="mt-6 space-y-3">
                            {orderData?.Hstatus === 'done' ? null : orderData?.Hstatus === 'arrive' ? (
                                <button
                                    type="button"
                                    onClick={handleExamDone}
                                    disabled={examSaving}
                                    className="w-full rounded-lg bg-black py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                                >
                                    {examSaving ? '更新中...' : '採点完了'}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleExamArrive}
                                    disabled={examSaving}
                                    className="w-full rounded-lg bg-black py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                                >
                                    {examSaving ? '更新中...' : '到着確認'}
                                </button>
                            )}
                            <button
                                type="button"
                                className="w-full rounded-lg border border-gray-200 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                onClick={() => setIsExamModalOpen(false)}
                                disabled={examSaving}
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isDiplomaModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-lg">
                        <div className="text-center text-lg font-semibold text-gray-800">認定証発送</div>
                        <div className="mt-6 space-y-4">
                            {orderData?.Dstatus === 'done' ? (
                                <div className="text-sm text-gray-600 text-center">発送済みです</div>
                            ) : orderData?.Dstatus === 'ready' ? (
                                <>
                                    <div className="text-sm text-gray-600 text-center">追跡番号を入力してください</div>
                                    <div className="flex items-center justify-center">
                                        <button
                                            type="button"
                                            className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                                                isAcrylicShipping
                                                    ? 'border-emerald-500 text-emerald-600'
                                                    : 'border-gray-300 text-gray-600'
                                            }`}
                                            onClick={() => setIsAcrylicShipping((prev) => !prev)}
                                        >
                                            {isAcrylicShipping ? 'アクリル版で送信中' : 'アクリル版に切替'}
                                        </button>
                                    </div>
                                    <input
                                        type="text"
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                        value={diplomaTrackingInput}
                                        onChange={(e) => setDiplomaTrackingInput(e.target.value)}
                                        placeholder="追跡番号を入力"
                                    />
                                </>
                            ) : (
                                <div className="text-sm text-gray-600 text-center">準備中です</div>
                            )}
                        </div>
                        <div className="mt-6 space-y-3">
                            {orderData?.Dstatus === 'ready' ? (
                                <button
                                    type="button"
                                    onClick={handleDiplomaShip}
                                    disabled={diplomaSaving || diplomaTrackingInput.trim() === ''}
                                    className="w-full rounded-lg bg-black py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                                >
                                    {diplomaSaving ? '発送中...' : '発送＆通知'}
                                </button>
                            ) : null}
                            <button
                                type="button"
                                className="w-full rounded-lg border border-gray-200 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                onClick={() => setIsDiplomaModalOpen(false)}
                                disabled={diplomaSaving}
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isReceiptModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-5xl rounded-2xl bg-white p-6 shadow-lg flex flex-col gap-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between">
                            <div className="text-lg font-semibold text-gray-800">領収書作成</div>
                            <button
                                type="button"
                                className="text-sm font-semibold text-gray-500 hover:text-gray-800"
                                onClick={() => setIsReceiptModalOpen(false)}
                                disabled={receiptSaving}
                            >
                                閉じる
                            </button>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-gray-700" htmlFor="receipt-application-date">
                                    申込日
                                </label>
                                <input
                                    id="receipt-application-date"
                                    type="date"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                    value={receiptApplicationDate}
                                    disabled
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-gray-700" htmlFor="receipt-issue-date">
                                    発行日
                                </label>
                                <input
                                    id="receipt-issue-date"
                                    type="date"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                    value={receiptIssueDate}
                                    onChange={(e) => setReceiptIssueDate(e.target.value)}
                                    disabled={receiptSaving}
                                />
                            </div>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-gray-700" htmlFor="receipt-customer-name">
                                    宛名
                                </label>
                                <input
                                    id="receipt-customer-name"
                                    type="text"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                    value={receiptCustomerName}
                                    onChange={(e) => setReceiptCustomerName(e.target.value)}
                                    disabled={receiptSaving}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-gray-700" htmlFor="receipt-remark">
                                    備考
                                </label>
                                <select
                                    id="receipt-remark"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                    value={receiptRemark}
                                    onChange={(e) => setReceiptRemark(e.target.value)}
                                    disabled={receiptSaving}
                                >
                                    {[
                                        'ーー',
                                        '※クレジットカード決済にて',
                                        '※スマホ決済にて',
                                        '※コンビニ決済にて',
                                        '※銀行振り込みにて',
                                    ].map((remark) => (
                                        <option key={remark} value={remark}>
                                            {remark}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {receiptDownloadUrl && (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                                <div className="font-semibold">領収書PDFを保存しました</div>
                                <a
                                    href={receiptDownloadUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-1 inline-block text-emerald-700 underline"
                                >
                                    ダウンロードリンクを開く
                                </a>
                            </div>
                        )}
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                            <style>{RECEIPT_STYLE}</style>
                            <div className="receipt-container bg-white" ref={receiptRef}>
                                <header>
                                    <h1>領収書</h1>
                                    <div className="date-section">
                                        <p>申込日: {formatReceiptDate(receiptApplicationDate)}</p>
                                        <p>発行日: {formatReceiptDate(receiptIssueDate)}</p>
                                    </div>
                                </header>

                                <section className="recipient">
                                    <h2 className="recipient-name">{receiptCustomerName || 'ー'} 様</h2>
                                    <p className="message">下記、正に領収いたしました。</p>
                                    <p className="subject">件名: 資格取得講座受講代金として</p>
                                </section>

                                <section className="total-amount">
                                    <span className="label">合計金額</span>
                                    <span className="price">{formatCurrency(receiptTotalAmount)}</span>
                                    <span className="tax-label">(税込)</span>
                                </section>

                                <div className="main-content">
                                    <table className="details-table">
                                        <thead>
                                            <tr>
                                                <th>No.</th>
                                                <th>摘要</th>
                                                <th>数量</th>
                                                <th>単価</th>
                                                <th>金額</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>1</td>
                                                <td>{shikakuName ? `${shikakuName}資格取得講座` : '資格取得講座'}</td>
                                                <td>1</td>
                                                <td>{formatCurrency(receiptSubtotal)}</td>
                                                <td>{formatCurrency(receiptSubtotal)}</td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <aside className="issuer-info">
                                        <div className="company-row">
                                            <p className="company-name">株式会社RIGWORKS</p>
                                            {receiptSealUrl ? (
                                                <img src={receiptSealUrl} alt="角印" className="receipt-seal" />
                                            ) : null}
                                        </div>
                                        <p>通信資格のラーキャリ</p>
                                        <p>〒619-0237</p>
                                        <p>京都府相楽郡精華町光台1丁目7</p>
                                        <p>けいはんなプラザ11F</p>
                                        <p>TEL: 0774-51-5638</p>
                                        <p>E-Mail: info@lear-caree.jp</p>
                                        <p>登録番号: T8130001069799</p>
                                    </aside>
                                </div>

                                <section className="summary-section">
                                    <div className="remarks">
                                        <p>備考: {receiptRemark}</p>
                                    </div>
                                    <table className="calculation-table">
                                        <tbody>
                                            <tr>
                                                <th>小計</th>
                                                <td>{formatCurrency(receiptSubtotal)}</td>
                                            </tr>
                                            <tr>
                                                <th>消費税</th>
                                                <td>{formatCurrency(receiptTax)}</td>
                                            </tr>
                                            <tr className="grand-total">
                                                <th>合計</th>
                                                <td>{formatCurrency(receiptTotalAmount)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </section>
                            </div>
                        </div>
                        <div className="flex flex-col gap-3">
                            <button
                                type="button"
                                className="w-full rounded-lg bg-black py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                                onClick={handleCreateReceiptPdf}
                                disabled={receiptSaving}
                            >
                                {receiptSaving ? '保存中...' : '領収書PDFを保存'}
                            </button>
                            <button
                                type="button"
                                className="w-full rounded-lg border border-gray-200 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                onClick={() => setIsReceiptModalOpen(false)}
                                disabled={receiptSaving}
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isMemoModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-lg">
                        <div className="text-center text-lg font-semibold text-gray-800">メモ作成</div>
                        <div className="mt-6 space-y-4">
                            <div className="text-sm text-gray-600">
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-500">作成日時</span>
                                    <span className="font-medium text-gray-800">{formatMemoDate(memoCreatedAt)}</span>
                                </div>
                            </div>
                            <div className="text-sm text-gray-600">
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-500">作成者</span>
                                    <span className="font-medium text-gray-800">
                                        {currentStaffName || auth.currentUser?.displayName || auth.currentUser?.email || auth.currentUser?.uid || '-'}
                                    </span>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <span className="text-sm font-semibold text-gray-700">タスクフラグ</span>
                                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="memo-task-flag"
                                            checked={memoTaskStatus === null}
                                            onChange={() => {
                                                setMemoTaskStatus(null);
                                                setMemoTaskLimit('');
                                                setMemoAccountId('');
                                            }}
                                        />
                                        ーー
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="memo-task-flag"
                                            checked={memoTaskStatus === 'active'}
                                            onChange={() => setMemoTaskStatus('active')}
                                        />
                                        タスク
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="memo-task-flag"
                                            checked={memoTaskStatus === 'done'}
                                            onChange={() => {
                                                setMemoTaskStatus('done');
                                            }}
                                        />
                                        タスク完了
                                    </label>
                                </div>
                            </div>
                            {(memoTaskStatus === 'active' || memoTaskStatus === 'done') && (
                                <>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700" htmlFor="memo-task-limit">
                                            タスク期日
                                        </label>
                                        <input
                                            id="memo-task-limit"
                                            type="date"
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                            value={memoTaskLimit}
                                            onChange={(e) => setMemoTaskLimit(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700" htmlFor="memo-task-account">
                                            担当者
                                        </label>
                                        <select
                                            id="memo-task-account"
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                            value={memoAccountId}
                                            onChange={(e) => setMemoAccountId(e.target.value)}
                                        >
                                            <option value="">担当者を選択</option>
                                            {adminUsers.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.displayName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-gray-700" htmlFor="memo-text">
                                    内容
                                </label>
                                <textarea
                                    id="memo-text"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                    rows={4}
                                    value={memoText}
                                    onChange={(e) => setMemoText(e.target.value)}
                                    placeholder="メモ内容を入力してください"
                                />
                            </div>
                        </div>
                        <div className="mt-6 space-y-3">
                            <button
                                type="button"
                                onClick={handleCreateMemo}
                                disabled={memoSaving || memoText.trim() === ''}
                                className="w-full rounded-lg bg-black py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                            >
                                {memoSaving ? '保存中...' : 'メモを保存'}
                            </button>
                            <button
                                type="button"
                                className="w-full rounded-lg border border-gray-200 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                onClick={() => setIsMemoModalOpen(false)}
                                disabled={memoSaving}
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isMemoDetailOpen && memoDetail && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
                    onClick={() => {
                        if (memoDetailSaving) return;
                        setIsMemoDetailEditing(false);
                        setIsMemoDetailOpen(false);
                    }}
                >
                    <div
                        className="w-[90%] max-w-[600px] min-w-[320px] min-h-[320px] max-h-[80vh] rounded-xl border border-gray-300 bg-white shadow-lg flex flex-col overflow-hidden"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {/** 削除は作成者本人のみ */}
                        {(() => {
                            const canDeleteMemo = canDeleteMemoByCurrentUser(memoDetail.memo);
                            return (
                                <>
                        <div className="bg-gray-600 text-white text-center py-3 text-lg font-semibold">
                            メモ詳細
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            <div className="grid gap-4 border-b border-gray-100 px-5 py-4 md:grid-cols-2">
                                <div>
                                    <div className="text-xs text-gray-400 mb-1">ユーザー</div>
                                    <div className="text-sm font-semibold text-gray-900">
                                        {memoDetail.targetUserName || '-'}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-400 mb-1">資格名</div>
                                    <div className="text-sm font-semibold text-gray-900">
                                        {memoDetail.shikakuName || '-'}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-400 mb-1">作成日時</div>
                                    <div className="text-sm font-semibold text-gray-900">
                                        {formatMemoDate(memoDetail.memo.CreateAt)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-400 mb-1">作成者</div>
                                    <div className="text-sm font-semibold text-gray-900">
                                        {memoDetail.createUserName || '-'}
                                    </div>
                                </div>
                            </div>
                            <div className="px-5 py-4">
                                <div className="text-xs text-gray-400 mb-2">内容</div>
                                {isMemoDetailEditing ? (
                                    <textarea
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                        rows={5}
                                        value={memoDetailText}
                                        onChange={(e) => setMemoDetailText(e.target.value)}
                                        placeholder="メモ内容を入力してください"
                                    />
                                ) : (
                                    <div className="text-sm font-semibold text-gray-900 whitespace-pre-wrap leading-6">
                                        {memoDetail.memo.text || '-'}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="bg-gray-50 px-5 py-4">
                            <div className="space-y-3">
                                {isMemoDetailEditing ? (
                                    <>
                                        <button
                                            type="button"
                                            className="w-full rounded-lg bg-emerald-400 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                                            onClick={handleUpdateMemoDetail}
                                            disabled={memoDetailSaving || memoDetailText.trim() === ''}
                                        >
                                            {memoDetailSaving ? '更新中...' : 'メモを更新'}
                                        </button>
                                        <button
                                            type="button"
                                            className="w-full rounded-lg bg-red-600 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                                            onClick={handleDeleteMemoDetail}
                                            disabled={memoDetailSaving || !canDeleteMemo}
                                        >
                                            {memoDetailSaving ? '処理中...' : 'メモを削除'}
                                        </button>
                                        <button
                                            type="button"
                                            className="w-full rounded-lg bg-gray-600 py-3 text-sm font-semibold text-white hover:opacity-80 disabled:cursor-not-allowed disabled:bg-gray-300"
                                            onClick={() => {
                                                setMemoDetailText(memoDetail.memo.text ?? '');
                                                setIsMemoDetailEditing(false);
                                            }}
                                            disabled={memoDetailSaving}
                                        >
                                            キャンセル
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            className="w-full rounded-lg bg-emerald-400 py-3 text-sm font-semibold text-white hover:opacity-80"
                                            onClick={() => setIsMemoDetailEditing(true)}
                                        >
                                            編集
                                        </button>
                                        {!canDeleteMemo && (
                                            <div className="text-center text-xs text-gray-500">※削除は作成者のみ可能です</div>
                                        )}
                                    </>
                                )}
                                <button
                                    type="button"
                                    className="w-full rounded-lg bg-gray-600 py-3 text-sm font-semibold text-white hover:opacity-80 disabled:cursor-not-allowed disabled:bg-gray-300"
                                    onClick={() => {
                                        setIsMemoDetailEditing(false);
                                        setIsMemoDetailOpen(false);
                                    }}
                                    disabled={memoDetailSaving}
                                >
                                    閉じる
                                </button>
                            </div>
                        </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
            {selectedMailMessage && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-[50vw] max-w-[50vw] h-[70vh] max-h-[70vh] rounded-2xl bg-white p-6 shadow-lg flex flex-col">
                        <div className="text-center text-lg font-semibold text-gray-800">メール詳細</div>
                        <div className="mt-6 flex-1 overflow-hidden rounded-xl border border-gray-100">
                            <div className="px-4 py-3 bg-gray-100 flex flex-wrap items-center gap-6 text-sm">
                                <div className="flex flex-col">
                                    <div className="text-xs text-gray-500">送受信日時</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {formatMemoDate(selectedMailMessage.createdAt)}
                                    </div>
                                </div>
                                <div className="flex flex-col">
                                    <div className="text-xs text-gray-500">送受信</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {selectedMailMessage.direction === 'out'
                                            ? '送信'
                                            : selectedMailMessage.direction === 'in'
                                              ? '受信'
                                              : '-'}
                                    </div>
                                </div>
                                <div className="flex flex-col">
                                    <div className="text-xs text-gray-500">顧客メール</div>
                                    <div className="text-sm font-medium text-gray-800">
                                        {selectedMailMessage.customerEmail || '-'}
                                    </div>
                                </div>
                            </div>
                            <div className="px-4 py-3 border-t border-gray-100">
                                <div className="text-xs text-gray-500 mb-1">件名</div>
                                <div className="text-sm font-medium text-gray-800">
                                    {selectedMailMessage.subject || '-'}
                                </div>
                            </div>
                            <div className="px-4 py-3 border-t border-gray-100">
                                <div className="text-xs text-gray-500 mb-1">本文</div>
                                <div className="text-sm font-medium text-gray-800 whitespace-pre-wrap max-h-[30vh] overflow-y-auto pr-2">
                                    {selectedMailMessage.body || '-'}
                                </div>
                            </div>
                        </div>
                        <div className="mt-6">
                            <button
                                type="button"
                                className="w-full rounded-lg border border-gray-200 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                onClick={() => setSelectedMailMessage(null)}
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
